<template>
  <AppModal
    v-model="isOpen"
    :title="modalTitle"
    theme="light"
    size="fullscreen"
    class="deliverable-modal-overflow wide-deliverable-modal ledger"
    :class="{ 'is-closing': isClosing }"
    :hide-cancel="true"
    :hide-confirm="true"
    :loading="isSaving"
  >
    <!-- Custom header with minimize and close buttons -->
    <template #header>
      <div class="modal-card-title">
        {{ modalTitle }}
        <DeliverableStatusBar
          v-if="isExisting && liveDeliverable"
          part="title"
          :deliverable="liveDeliverable"
          :deliverable-form="form"
          :project="liveProject"
          :company="company"
        />
        <!-- Clone indicator -->
        <span v-if="isClonedDeliverable" class="tag is-warning ml-2" title="This is a cloned deliverable">
          <span class="icon is-small mr-1">
            <font-awesome-icon :icon="['fal', 'clone']" />
          </span>
          Cloned
        </span>
      </div>
      <div class="modal-header-buttons">
        <button
          class="button is-small is-warning is-rounded modal-button-circular"
          @click="minimizeModal"
          title="Minimize deliverable"
          :disabled="!isExisting"
        >
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'minus']" />
          </span>
        </button>
        <button
          class="button is-small is-danger is-rounded modal-button-circular"
          @click="closeModal"
          title="Close"
        >
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'times']" />
          </span>
        </button>
      </div>
    </template>

    <!-- Main Two-Column Layout -->
    <div class="columns is-gapless is-marginless deliverable-details-container">
      <!-- Left Column with Tabs -->
      <div class="column form-column" :class="isFullWidthTab ? 'is-12' : 'is-6'" :style="activeTab === 'review' ? 'padding: 1.5rem 1.5rem 0 1.5rem !important;' : isFullWidthTab ? 'padding: 1.5rem 1.5rem 1.5rem 1.5rem !important;' : 'padding: 1.5rem !important;'">
        <!-- Tabs for existing deliverables -->
        <div v-if="isExisting" class="toolbar">
          <div class="filters" role="tablist">
            <button
              v-for="tab in visibleTabs"
              :key="tab.key"
              class="filter"
              :class="{ 'is-active': activeTab === tab.key }"
              role="tab"
              @click="selectTab(tab.key)"
            >
              <span class="icon is-small">
                <font-awesome-icon
                  :icon="['fal', tab.icon]"
                  :active-icon="['fadl', tab.icon]"
                  :active="activeTab === tab.key"
                />
              </span>
              {{ tab.label }}
            </button>
          </div>
          <div class="toolbar-extras" :class="{ 'is-own-row': !isFullWidthTab }">
            <DeliverableStatusBar
              v-if="liveDeliverable && activeTab !== 'assets'"
              part="toolbar"
              :deliverable="liveDeliverable"
              :deliverable-form="form"
              :project="liveProject"
              :company="company"
            />
            <!-- Review tab teleports Finalization / Skip Internal Review here -->
            <div id="deliverable-toolbar-extras" class="toolbar-review-extras"></div>
          </div>
        </div>

        <!-- Details Tab Content -->
        <div v-show="!isExisting || activeTab === 'details'" class="tab-content">
        <!-- View Mode -->
        <div v-if="isExisting && !isEditMode" class="deliverable-view">
        <!-- Title with Type Tag and Edit Button -->
        <div class="is-flex is-justify-content-space-between is-align-items-start mb-5">
          <div style="min-width: 0; flex: 1;">
            <h2 class="title is-4 mb-0">{{ form.title }}</h2>
            <p v-if="props.company?.name" class="has-text-grey is-size-6 mt-1">
              {{ props.company.name }}
            </p>
            <div class="tags mt-2">
              <span class="tag" :style="{ backgroundColor: getTypeColor(form.type) + '20', borderColor: getTypeColor(form.type), color: getTypeColor(form.type) }">
                <span class="icon is-small mr-1">
                  <font-awesome-icon :icon="['fal', getTypeIcon(form.type)]" />
                </span>
                {{ getTypeLabel(form.type) }}
              </span>
              <span v-if="form.weight != null" class="tag is-warning">
                {{ getDeliverableWeightLabel(form.weight) }} pt{{ form.weight !== 1 ? 's' : '' }}
              </span>
              <span v-if="form.isPrimaryEpisodeDeliverable" class="tag is-primary is-small">
                <span class="icon is-small mr-1">
                  <font-awesome-icon :icon="['fal', 'podcast']" />
                </span>
                Primary Episode
              </span>
            </div>
          </div>
          <button
            class="button is-primary ml-3"
            @click="isEditMode = true"
            title="Edit"
            style="flex-shrink: 0;"
          >
            <span class="icon">
              <font-awesome-icon :icon="['fal', 'edit']" />
            </span>
          </button>
        </div>

        <!-- View Original Deliverable (for clones) -->
        <div v-if="isClonedDeliverable" class="mb-4">
          <div class="notification is-info is-light">
            <div class="is-flex is-align-items-center is-justify-content-space-between">
              <div>
                <p class="is-size-7 has-text-weight-semibold mb-1">
                  <span class="icon is-small mr-1">
                    <font-awesome-icon :icon="['fal', 'link']" />
                  </span>
                  Cloned from original deliverable
                </p>
                <p v-if="originalDeliverableInfo && !originalDeliverableDeleted" class="is-size-7">
                  {{ originalDeliverableInfo.title || 'Loading...' }}
                </p>
                <p v-if="originalDeliverableDeleted" class="is-size-7 has-text-danger">
                  <span class="icon is-small mr-1">
                    <font-awesome-icon :icon="['fal', 'exclamation-triangle']" />
                  </span>
                  Original deliverable has been deleted
                </p>
              </div>
              <button
                v-if="!originalDeliverableDeleted"
                class="button is-small is-primary"
                @click="viewOriginalDeliverable"
                :disabled="loadingOriginal"
                :class="{ 'is-loading': loadingOriginal }"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'external-link']" />
                </span>
                <span>View Original</span>
              </button>
            </div>
          </div>
        </div>

        <!-- View Mode Content -->
        <div class="columns is-multiline">
          <!-- Title removed from here since it's now at the top -->

          <!-- Assigned User and Due Date at the top with inline editing -->
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Assigned To</label>
              <div class="is-flex is-align-items-center">
                <UserAvatarSelect
                  v-model="form.assignedUserId"
                  size="medium"
                  placeholder="Assign to team member"
                  :clearable="false"
                  :disabled="false"
                  @select="handleInlineUserAssigned"
                />
                <span v-if="assignedUser" class="ml-3 has-text-weight-medium">
                  {{ userDisplayName(assignedUser) }}
                </span>
                <span v-else class="ml-3 has-text-grey is-size-7">
                  Click to assign a team member
                </span>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Primary Editor</label>
              <div class="is-flex is-align-items-center">
                <UserAvatarSelect
                  v-model="form.primaryEditorId"
                  size="medium"
                  placeholder="Assign primary editor"
                  :clearable="false"
                  :disabled="false"
                  @select="handleInlinePrimaryEditorAssigned"
                />
                <span v-if="primaryEditor" class="ml-3 has-text-weight-medium">
                  {{ userDisplayName(primaryEditor) }}
                </span>
                <span v-else class="ml-3 has-text-grey is-size-7">
                  Click to assign primary editor
                </span>
              </div>
            </div>
          </div>
          <!-- Stage and Status on the same row -->
          <div class="column is-6">
            <div class="detail-item mb-4 inline-editable">
              <label class="label is-small">Stage</label>
              <div v-if="!isEditingStage" class="clickable-field" @click="isEditingStage = true">
                <span
                  class="tag"
                  :class="getStageClass(form.stage)"
                >
                  <span class="icon is-small mr-1">
                    <font-awesome-icon :icon="['fal', getStageIcon(form.stage)]" />
                  </span>
                  {{ getStageLabel(form.stage) }}
                  <span class="icon ml-2 edit-hint">
                    <font-awesome-icon :icon="['fal', 'edit']" />
                  </span>
                </span>
              </div>
              <div v-else class="is-flex is-align-items-center" style="position: relative; z-index: 100;">
                <SearchableSelect
                  v-model="tempStage"
                  :items="stageOptions"
                  value-field="value"
                  display-field="label"
                  placeholder="Select stage..."
                  :hide-search="true"
                  :show-icons="true"
                  icon-field="icon"
                  style="flex: 1; min-width: 200px;"
                  class="stage-inline-select"
                >
                  <template #item="{ item }">
                    <div class="is-flex is-align-items-center">
                      <span class="icon mr-2" :style="{ color: item.color }">
                        <font-awesome-icon :icon="['fal', item.icon]" />
                      </span>
                      <span>{{ item.label }}</span>
                    </div>
                  </template>
                </SearchableSelect>
                <button class="button is-small is-primary ml-2" @click="saveInlineStage($event)">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'check']" />
                  </span>
                </button>
                <button class="button is-small ml-1" @click="cancelInlineStage">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'times']" />
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="detail-item mb-4 inline-editable">
              <label class="label is-small">
                Status
                <span v-if="isStatusEditingDisabled" class="has-text-grey is-size-7 ml-1">(Change stage to edit)</span>
              </label>
              <div v-if="!isEditingStatus" class="clickable-field" :class="{ 'is-disabled': isStatusEditingDisabled }" @click="!isStatusEditingDisabled && (isEditingStatus = true)">
                <span
                  class="tag"
                  :class="[getStatusClass(form.status), { 'is-disabled': isStatusEditingDisabled }]"
                >
                  <span class="icon is-small mr-1">
                    <font-awesome-icon :icon="['fal', getStatusIcon(form.status)]" />
                  </span>
                  {{ getStatusLabel(form.status) }}
                  <span v-if="!isStatusEditingDisabled" class="icon ml-2 edit-hint">
                    <font-awesome-icon :icon="['fal', 'edit']" />
                  </span>
                  <span v-else class="icon ml-2" style="opacity: 0.5;">
                    <font-awesome-icon :icon="['fal', 'lock']" />
                  </span>
                </span>
              </div>
              <div v-else class="is-flex is-align-items-center" style="position: relative; z-index: 100;">
                <SearchableSelect
                  v-model="tempStatus"
                  :items="statusOptions"
                  value-field="value"
                  display-field="label"
                  placeholder="Select status..."
                  :hide-search="true"
                  :show-icons="true"
                  icon-field="icon"
                  style="flex: 1; min-width: 200px;"
                  class="status-inline-select"
                >
                  <template #item="{ item }">
                    <div class="is-flex is-align-items-center">
                      <span class="icon mr-2" :style="{ color: item.color }">
                        <font-awesome-icon :icon="['fal', item.icon]" />
                      </span>
                      <span>{{ item.label }}</span>
                    </div>
                  </template>
                </SearchableSelect>
                <button class="button is-small is-primary ml-2" @click="saveInlineStatus($event)">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'check']" />
                  </span>
                </button>
                <button class="button is-small ml-1" @click="cancelInlineStatus">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'times']" />
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Due Date -->
          <div class="column is-6">
            <div class="detail-item mb-4 inline-editable">
              <label class="label is-small">Due Date</label>
              <div v-if="!isEditingDueDate" class="clickable-field" @click="isEditingDueDate = true">
                <p>
                  {{ form.dueDate ? formatEventDate(form.dueDate) : 'No due date' }}
                  <span class="icon ml-2 edit-hint">
                    <font-awesome-icon :icon="['fal', 'edit']" />
                  </span>
                </p>
              </div>
              <div v-else class="is-flex is-align-items-center">
                <VueDatePicker
                  v-model="tempDueDate"
                  :enable-time-picker="false"
                  :min-date="new Date()"
                  format="MMM dd, yyyy"
                  placeholder="Select due date..."
                  :show-now-button="true"
                  now-button-label="Today"
                  style="flex: 1;"
                />
                <button class="button is-small is-primary ml-2" @click="saveInlineDueDate($event)">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'check']" />
                  </span>
                </button>
                <button class="button is-small ml-1" @click="cancelInlineDueDate">
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'times']" />
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Aspect Ratio and Runtime -->
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Aspect Ratio</label>
              <p>{{ getAspectRatioLabel(form.aspectRatio) }}</p>
            </div>
          </div>
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Runtime</label>
              <p>{{ form.runtime || 'Not specified' }}</p>
            </div>
          </div>

          <!-- Production Events -->
          <div class="column is-12">
            <div class="detail-item mb-4">
              <label class="label is-small">Linked Production Events</label>
              <div v-if="selectedEvents.length > 0" class="tags">
                <router-link
                  v-for="event in selectedEvents"
                  :key="event.id"
                  :to="{ name: 'admin-calendar-event', params: { id: event.id } }"
                  class="tag is-clickable"
                  :style="{ 
                    backgroundColor: `${event.color}15`,
                    borderColor: event.color,
                    color: event.color
                  }"
                >
                  <span class="icon is-small mr-1">
                    <font-awesome-icon :icon="event.typeIcon" />
                  </span>
                  {{ event.title }}
                  <span v-if="!event.isCurrentProject" class="is-size-7 ml-1">({{ event.projectName }})</span>
                </router-link>
              </div>
              <p v-else class="has-text-grey">No linked events</p>
            </div>
          </div>

          <!-- Description -->
          <div class="column is-12">
            <div class="detail-item">
              <label class="label is-small">Description</label>
              <p class="description-text">{{ form.description || 'No description provided' }}</p>
            </div>
          </div>
        </div>

        <!-- Dependencies & Linking Section -->
        <DeliverableDependencies
          v-if="isExisting && deliverableProjectId"
          :deliverable-id="props.deliverable.id"
          :project-id="deliverableProjectId"
          :dependencies="form.dependencies || []"
          :linked-deliverable-ids="form.linkedDeliverableIds || []"
          @update:dependencies="saveDependencies"
          @update:linkedDeliverableIds="saveLinkedDeliverableIds"
          @navigate="navigateToLinkedDeliverable"
        />

        <!-- Action Buttons Section -->
        <DeliverableActionButtons
          v-if="liveDeliverable?.id"
          :deliverable="liveDeliverable"
          :company-id="props.company?.id"
          @action-executed="handleActionExecuted"
        />
      </div>

        <!-- Edit/Create Mode -->
        <div v-else class="deliverable-form">
        <!-- Title -->
        <div class="detail-item mb-4">
          <label class="label is-small">
            Title <span class="has-text-danger">*</span>
          </label>
          <div class="control">
            <input
              v-model="form.title"
              class="input"
              type="text"
              placeholder="Enter deliverable title..."
              :class="{ 'is-danger': errors.title }"
            />
          </div>
          <p v-if="errors.title" class="help is-danger">{{ errors.title }}</p>
        </div>

        <!-- Type and Weight Row -->
        <div class="columns">
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">
                Type <span class="has-text-danger">*</span>
              </label>
              <SearchableSelect
                v-model="form.type"
                :items="typeOptions"
                value-field="value"
                display-field="label"
                placeholder="Select type..."
                :hide-search="true"
                :show-icons="true"
                icon-field="icon"
              />
            </div>
          </div>
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">Weight</label>
              <SearchableSelect
                v-model="form.weight"
                :items="weightOptions"
                value-field="value"
                display-field="label"
                placeholder="Select weight..."
                :hide-search="true"
              />
            </div>
          </div>
        </div>

        <!-- Stage and Status Row -->
        <div class="columns">
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">
                Stage <span class="has-text-danger">*</span>
              </label>
              <SearchableSelect
                v-model="form.stage"
                :items="stageOptions"
                value-field="value"
                display-field="label"
                placeholder="Select stage..."
                :hide-search="true"
                :show-icons="true"
                icon-field="icon"
              >
                <template #item="{ item }">
                  <div class="is-flex is-align-items-center">
                    <span class="icon mr-2" :style="{ color: item.color }">
                      <font-awesome-icon :icon="['fal', item.icon]" />
                    </span>
                    <span>{{ item.label }}</span>
                  </div>
                </template>
              </SearchableSelect>
            </div>
          </div>
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">
                Status <span class="has-text-danger">*</span>
              </label>
              <SearchableSelect
                v-model="form.status"
                :items="statusOptions"
                value-field="value"
                display-field="label"
                placeholder="Select status..."
                :hide-search="true"
                :show-icons="true"
                icon-field="icon"
                :disabled="isStatusEditingDisabled"
              >
                <template #item="{ item }">
                  <div class="is-flex is-align-items-center">
                    <span class="icon mr-2" :style="{ color: item.color }">
                      <font-awesome-icon :icon="['fal', item.icon]" />
                    </span>
                    <span>{{ item.label }}</span>
                  </div>
                </template>
              </SearchableSelect>
              <p v-if="isStatusEditingDisabled" class="help">Change stage to edit status</p>
            </div>
          </div>
        </div>

        <!-- Assigned User and Primary Editor Row -->
        <div class="columns">
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Assigned To</label>
              <div class="is-flex is-align-items-center">
                <UserAvatarSelect
                  v-model="form.assignedUserId"
                  size="medium"
                  placeholder="Assign to team member"
                  :clearable="isEditMode"
                  :disabled="!isEditMode"
                  @select="handleUserAssigned"
                />
                <span v-if="assignedUser" class="ml-3 has-text-weight-medium">
                  {{ userDisplayName(assignedUser) }}
                </span>
                <span v-else class="ml-3 has-text-grey">
                  Click to assign a team member
                </span>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Primary Editor</label>
              <div class="is-flex is-align-items-center">
                <UserAvatarSelect
                  v-model="form.primaryEditorId"
                  size="medium"
                  placeholder="Assign primary editor"
                  :clearable="true"
                  :disabled="false"
                  @select="handleUserAssigned"
                />
                <span v-if="!form.primaryEditorId" class="ml-3 has-text-grey">
                  Click to assign primary editor
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Due Date Row -->
        <div class="columns">
          <div class="column is-6">
            <div class="detail-item mb-4">
              <label class="label is-small">Due Date</label>
              <div class="control">
                <VueDatePicker
                  v-model="form.dueDate"
                  :enable-time-picker="false"
                  :min-date="new Date()"
                  format="MMM dd, yyyy"
                  placeholder="Select due date..."
                  :show-now-button="true"
                  now-button-label="Today"
                  :class="{ 'is-danger': errors.dueDate }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Primary Episode Deliverable (only for episodes) -->
        <div v-if="liveProject?.type === 'episode'" class="detail-item mb-4">
          <label class="checkbox is-size-7">
            <input v-model="form.isPrimaryEpisodeDeliverable" type="checkbox" />
            Primary Episode Deliverable
          </label>
          <p class="help">When in review, this deliverable pulls the episode's title, thumbnail, and description.</p>
        </div>

        <!-- Aspect Ratio and Runtime Row -->
        <div class="columns">
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">Aspect Ratio</label>
              <SearchableSelect
                v-model="form.aspectRatio"
                :items="aspectRatioOptions"
                value-field="value"
                display-field="label"
                placeholder="Select aspect ratio..."
                :hide-search="true"
              />
            </div>
          </div>
          <div class="column">
            <div class="detail-item mb-4">
              <label class="label is-small">Runtime (mm:ss)</label>
              <div class="control">
                <input
                  v-model="form.runtime"
                  class="input"
                  type="text"
                  placeholder="00:00"
                  pattern="[0-9]{1,2}:[0-9]{2}"
                  @input="formatRuntime"
                />
              </div>
              <p class="help">Format: mm:ss (e.g., 02:30)</p>
            </div>
          </div>
        </div>

        <!-- Production Events -->
        <div class="detail-item mb-4">
          <label class="label is-small">Linked Production Events</label>
          <SearchableSelect
            v-model="form.productionEventIds"
            :items="availableEvents"
            :multiple="true"
            value-field="id"
            display-field="title"
            placeholder="Select production events..."
            search-placeholder="Search events..."
            no-results-text="No events found"
            :show-icons="true"
            icon-field="typeIcon"
          >
            <template #item="{ item }">
              <div class="is-flex is-align-items-center">
                <span class="icon mr-2" :style="{ color: item.color }">
                  <font-awesome-icon :icon="item.typeIcon" />
                </span>
                <div class="is-flex-grow-1">
                  <div class="is-flex is-align-items-center is-justify-content-space-between">
                    <div>
                      {{ item.title }}
                    </div>
                    <span class="is-size-7 has-text-grey ml-2">
                      {{ formatEventDateTime(item.date) }}
                    </span>
                  </div>
                  <div class="is-size-7 has-text-grey">
                    {{ item.projectName }}
                  </div>
                </div>
              </div>
            </template>
          </SearchableSelect>
          
          <!-- Display selected events as tags -->
          <div v-if="selectedEvents.length > 0" class="tags mt-2">
            <span
              v-for="event in selectedEvents"
              :key="event.id"
              class="tag"
              :style="{ 
                backgroundColor: `${event.color}15`,
                borderColor: event.color,
                color: event.color
              }"
            >
              <span class="icon is-small mr-1">
                <font-awesome-icon :icon="event.typeIcon" />
              </span>
              {{ event.title }}
              <span v-if="!event.isCurrentProject" class="is-size-7 ml-1">({{ event.projectName }})</span>
              <button
                class="delete is-small ml-2"
                @click="removeEvent(event.id)"
              ></button>
            </span>
          </div>
        </div>

        <!-- Description -->
        <div class="detail-item mb-4">
          <label class="label is-small">Description</label>
          <div class="control">
            <textarea
              v-model="form.description"
              class="textarea"
              rows="3"
              placeholder="Enter deliverable description..."
            ></textarea>
          </div>
        </div>
        
        <!-- Action Buttons (in edit mode or creating new) -->
        <div v-if="isEditMode || !isExisting" class="is-flex is-justify-content-space-between is-align-items-center mt-5 pt-4" style="border-top: 1px solid var(--bulma-border-weak);">
          <!-- Delete and Clone buttons on the left -->
          <div v-if="isExisting" class="buttons mb-0">
            <button
              class="button is-danger"
              @click="confirmDelete"
              title="Delete Deliverable"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fal', 'trash']" />
              </span>
            </button>
            <!-- Clone & Detach button - only show for non-cloned deliverables -->
            <button
              v-if="!isClonedDeliverable"
              class="button is-info"
              @click="handleCloneAndDetach"
              :class="{ 'is-loading': isCloning }"
              :disabled="isCloning"
              title="Clone & Detach from Project"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fal', 'clone']" />
              </span>
            </button>
          </div>
          <div v-else></div> <!-- Empty div to maintain flex spacing -->

          <!-- Cancel and Save buttons on the right -->
          <div class="buttons mb-0">
            <button
              class="button is-text"
              @click="handleCancel"
            >
              Cancel
            </button>
            <button
              class="button is-primary"
              @click="saveDeliverable"
              :class="{ 'is-loading': isSaving }"
              :disabled="!isValid"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fal', 'save']" />
              </span>
              <span>{{ isExisting ? 'Save Changes' : 'Create Deliverable' }}</span>
            </button>
          </div>
        </div>
        </div>
        </div>

        <!-- Project Brief Tab Content -->
        <div v-show="activeTab === 'brief'" class="tab-content">
          <div class="brief-container">
            <!-- View Project Button - only show for non-cloned deliverables -->
            <div v-if="!isClonedDeliverable" class="level mb-4">
              <div class="level-left">
                <div class="level-item">
                  <!-- Empty left side for alignment -->
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <button
                    class="button is-primary"
                    @click="viewFullProject"
                  >
                    <span class="icon">
                      <font-awesome-icon :icon="['fal', 'arrow-right-from-bracket']" />
                    </span>
                    <span>View Full Project</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- For cloned deliverables, show origin info -->
            <div v-if="isClonedDeliverable" class="level mb-4">
              <div class="level-left">
                <div class="level-item">
                  <span v-if="originalProjectName" class="tag is-info">
                    <span class="icon is-small mr-1">
                      <font-awesome-icon :icon="['fal', 'link']" />
                    </span>
                    Brief from original project: {{ originalProjectName }}
                  </span>
                  <span v-else-if="loadingOriginalBrief" class="tag">
                    <BrandLoader :size="'lg'" />
                    Loading...
                  </span>
                </div>
              </div>
            </div>

            <!-- Loading state for dynamic brief -->
            <div v-if="isClonedDeliverable && loadingOriginalBrief" class="has-text-centered py-6">
              <BrandLoader :size="'3x'" />
              <p class="mt-2 has-text-grey">Loading project brief...</p>
            </div>

            <!-- Error state for dynamic brief -->
            <div v-else-if="isClonedDeliverable && originalBriefError" class="notification is-warning">
              <span class="icon mr-2">
                <font-awesome-icon :icon="['fal', 'exclamation-triangle']" />
              </span>
              {{ originalBriefError }}
            </div>

            <!-- Existing ProjectBrief component - use effectiveBrief for clones -->
            <ProjectBrief
              v-else-if="effectiveBrief && effectiveBrief.length > 0"
              :brief="effectiveBrief"
              :status="effectiveProjectStatus"
              :is-admin="false"
              :company-id="props.company?.id"
              :project-id="effectiveProjectId"
              :readonly="true"
              :hide-header="true"
              :show-section-headers="true"
              :assets-store="clientAssetsStore"
            />
            <div v-else class="has-text-grey has-text-centered py-6">
              <span class="icon is-large">
                <font-awesome-icon :icon="['fal', 'file-lines']" size="2x" />
              </span>
              <p class="mt-2">No project brief available</p>
            </div>
          </div>
        </div>

        <!-- Assets Tab -->
        <div v-show="activeTab === 'assets'" class="tab-content">
          <div class="assets-tab-content">
            <!-- Download All Button -->
            <div v-if="deliverableAssets.length > 1" class="mb-4">
              <button
                class="button is-primary"
                @click="downloadAllAssets"
                :class="{ 'is-loading': isDownloadingAll }"
                :disabled="isDownloadingAll"
              >
                <span class="icon">
                  <font-awesome-icon :icon="['fal', 'download']" />
                </span>
                <span>Download All ({{ deliverableAssets.length }} files)</span>
              </button>
            </div>

            <div
              class="assets-grid"
              :class="{ 'single-column': deliverableAssets.length === 1 }"
            >
              <div
                v-for="asset in pagedDeliverableAssets"
                :key="asset.id"
                class="asset-card-wrapper"
              >
                <MediaCard :item="asset" :preview-all="true" @preview="handleAssetPreview" />
                <button
                  class="download-overlay-btn"
                  @click.stop="downloadSingleAsset(asset)"
                  :class="{ 'is-loading': downloadingAssetIds.includes(asset.id) }"
                  :disabled="downloadingAssetIds.includes(asset.id)"
                  title="Download"
                >
                  <span class="icon">
                    <font-awesome-icon :icon="['fal', 'download']" />
                  </span>
                </button>
                <button
                  v-if="canExtractAudio(asset)"
                  class="download-overlay-btn is-extract-audio"
                  @click.stop="extractAudio(asset)"
                  :class="{ 'is-loading': isExtracting(asset.id) }"
                  :disabled="isExtracting(asset.id) || hasExtractedAudio(asset, clientAssetsStore.items)"
                  :title="hasExtractedAudio(asset, clientAssetsStore.items) ? 'MP3 already in library' : 'Save audio as MP3 to library'"
                >
                  <span class="icon">
                    <font-awesome-icon
                      :icon="['fal', 'waveform-lines']"
                      :active-icon="['fas', 'circle-notch']"
                      :active="isExtracting(asset.id)"
                      :spin="isExtracting(asset.id)"
                    />
                  </span>
                </button>
              </div>
            </div>

            <!-- Pagination (same pattern as ProjectAssetsTab) -->
            <div v-if="deliverableAssets.length > ASSET_PAGE_SIZES[0]" class="assets-pagination">
              <div class="is-flex is-align-items-center">
                <span class="has-text-grey is-size-7 mr-2">Show:</span>
                <div class="select is-small">
                  <select v-model.number="assetsPerPage" style="min-width: 60px">
                    <option v-for="size in ASSET_PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
                  </select>
                </div>
                <span class="has-text-grey is-size-7 ml-2">
                  {{ assetPageStart + 1 }}–{{ Math.min(assetPageStart + assetsPerPage, deliverableAssets.length) }} of {{ deliverableAssets.length }}
                </span>
              </div>
              <nav v-if="assetTotalPages > 1" class="pagination is-right is-small" role="navigation" aria-label="pagination" style="margin: 0">
                <ul class="pagination-list">
                  <li>
                    <button
                      class="pagination-link"
                      :disabled="assetPage === 1"
                      title="Previous page"
                      aria-label="Previous page"
                      @click="assetPage--"
                    >
                      <span class="icon is-small"><font-awesome-icon :icon="['fas', 'chevron-left']" /></span>
                    </button>
                  </li>
                  <li v-for="page in assetPageNumbers" :key="page">
                    <button
                      class="pagination-link"
                      :class="{ 'is-current': page === assetPage }"
                      :aria-label="`Page ${page}`"
                      :aria-current="page === assetPage ? 'page' : undefined"
                      @click="assetPage = page"
                    >{{ page }}</button>
                  </li>
                  <li>
                    <button
                      class="pagination-link"
                      :disabled="assetPage === assetTotalPages"
                      title="Next page"
                      aria-label="Next page"
                      @click="assetPage++"
                    >
                      <span class="icon is-small"><font-awesome-icon :icon="['fas', 'chevron-right']" /></span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <!-- Activity Tab Content -->
        <div v-show="isExisting && activeTab === 'activity'" class="tab-content activity-tab-content">
          <DeliverableActivityTimeline
            v-if="deliverable?.id"
            :deliverable-id="deliverable.id"
            :deliverable="liveDeliverable"
            :compact="false"
          />
        </div>

        <!-- Review Tab Content -->
        <div v-show="isExisting && activeTab === 'review'" class="tab-content review-tab-content">
          <DeliverableReviewTab v-if="reviewTabActivated" :is-active="activeTab === 'review'" :deliverable="liveDeliverable" :deliverable-form="form" :company="company" :project="liveProject" :final-assets="deliverableAssets" />
        </div>
      </div>

      <!-- Discussion/Conversation Column -->
      <div v-show="!isFullWidthTab" class="column is-6 discussion-column">
        <div class="chat-container">
          <div class="notification is-primary mb-0" style="box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);">
            <span class="icon-text has-text-primary">
              <span class="icon">
                <font-awesome-icon :icon="['fal', 'comment-dots']" />
              </span>
              <span class="has-text-weight-semibold">Discussion</span>
            </span>
          </div>

          <div class="chat-messages" ref="chatMessages">
            <!-- Loading state -->
            <div v-if="loadingMessages" class="has-text-centered py-4">
              <BrandLoader :size="'2x'" />
              <p class="mt-2 is-size-7 has-text-grey">Loading messages...</p>
            </div>

            <!-- Empty state -->
            <div v-else-if="messages.length === 0 && isExisting" class="has-text-centered py-5">
              <span class="icon is-large has-text-grey-light">
                <font-awesome-icon :icon="['fal', 'comments']" size="2x" />
              </span>
              <p class="mt-2 is-size-7 has-text-grey">No messages yet. Start the conversation!</p>
            </div>

            <!-- Messages list -->
            <div v-else>
              <template v-for="dayGroup in messagesGroupedByDay" :key="dayGroup.dateKey">
                <!-- Day divider -->
                <div class="day-divider">
                  <span class="day-divider-text">{{ dayGroup.dateDisplay }}</span>
                </div>

                <!-- Messages for this day -->
                <template v-for="message in dayGroup.messages" :key="message.id">
                  <!-- Deleted message (centered, no user attribution) -->
                  <div v-if="message.deleted" class="deleted-message-centered">
                    <span class="deleted-text">message deleted</span>
                  </div>

                <!-- Regular message -->
                <div
                  v-else
                  class="message-row"
                  :class="{
                    'is-mine': message.userId === userAuthStore.user.id,
                    'is-other': message.userId !== userAuthStore.user.id,
                    'is-editing': editingMessageId === message.id
                  }"
                  :data-message-id="message.id"
                >
                  <!-- Avatar (left for others) -->
                  <div v-if="message.userId !== userAuthStore.user.id" class="message-avatar">
                    <div class="avatar-circle">
                      <BunnyImage
                        :fallback="getUserAvatar(message.userId)"
                        :alt="getUserFullName(message.userId, message.userName)"
                        img-class="avatar-image"
                      >
                        <template #placeholder>
                          <span>{{ getUserInitials(getUserFullName(message.userId, message.userName)) }}</span>
                        </template>
                      </BunnyImage>
                    </div>
                  </div>

                  <!-- Message container -->
                  <div class="message-container">
                    <!-- Sender name and time header -->
                    <div class="message-header">
                      <span class="message-sender">{{ getUserFullName(message.userId, message.userName) }}</span>
                      <span class="message-time">{{ formatMessageTime(message.timestamp) }}</span>
                    </div>

                    <!-- Message bubble with hover actions -->
                    <div class="message-bubble-wrapper">
                      <!-- Hover actions overlay -->
                      <div class="message-hover-actions">
                        <!-- Show delete confirmation -->
                        <template v-if="deleteConfirmMessageId === message.id">
                          <span class="confirm-text">Are you sure?</span>
                          <button
                            class="hover-action-btn confirm-btn"
                            @click="confirmDeleteMessage(message)"
                            title="Yes, delete"
                          >
                            <font-awesome-icon :icon="['fal', 'check']" />
                          </button>
                          <button
                            class="hover-action-btn cancel-btn"
                            @click="cancelDeleteMessage"
                            title="Cancel"
                          >
                            <font-awesome-icon :icon="['fal', 'times']" />
                          </button>
                        </template>

                        <!-- Show emoji options when picker is active -->
                        <template v-else-if="showEmojiPicker === message.id">
                          <button
                            v-for="emoji in ['👍', '❤️', '😂', '😄', '🎉', '😕', '🔥', '👀']"
                            :key="emoji"
                            @click="toggleReaction(message, emoji)"
                            class="hover-action-btn emoji-btn"
                            :title="emoji"
                          >
                            {{ emoji }}
                          </button>
                          <button
                            class="hover-action-btn"
                            @click="showEmojiPicker = null"
                            title="Close"
                          >
                            <font-awesome-icon :icon="['fal', 'times']" />
                          </button>
                        </template>

                        <!-- Default action buttons -->
                        <template v-else>
                          <!-- Reaction button -->
                          <button
                            class="hover-action-btn"
                            @click="showEmojiPicker = message.id"
                            title="React"
                          >
                            <font-awesome-icon :icon="['fal', 'smile']" />
                          </button>

                          <!-- Edit/Delete for own messages -->
                          <template v-if="message.userId === userAuthStore.user.id">
                            <button class="hover-action-btn" @click="startEditMessage(message)" title="Edit">
                              <font-awesome-icon :icon="['fal', 'edit']" />
                            </button>
                            <button class="hover-action-btn" @click="deleteMessage(message)" title="Delete">
                              <font-awesome-icon :icon="['fal', 'trash']" />
                            </button>
                          </template>

                        </template>
                      </div>

                      <!-- Read Receipts (left side of bubble for own messages) -->
                      <div v-if="getMessageReadReceipts(message).length > 0" class="read-receipt-avatars-bubble">
                        <div
                          v-for="reader in getMessageReadReceipts(message).slice(0, 5)"
                          :key="reader.userId"
                          class="read-receipt-avatar"
                          :title="`Seen by ${reader.userName}`"
                        >
                          <BunnyImage :fallback="reader.userAvatar" :alt="reader.userName" img-class="avatar-image">
                            <template #placeholder>
                              <span class="avatar-initials">{{ getUserInitials(reader.userName) }}</span>
                            </template>
                          </BunnyImage>
                        </div>

                        <!-- Show count if more than 5 people have read -->
                        <div
                          v-if="getMessageReadReceipts(message).length > 5"
                          class="read-receipt-more"
                          :title="getMessageReadReceipts(message).map(r => r.userName).join(', ')"
                        >
                          +{{ getMessageReadReceipts(message).length - 5 }}
                        </div>
                      </div>

                      <!-- Message content (stacks bubble + image vertically) -->
                      <div class="message-content">
                        <div class="message-bubble" v-if="message.message">
                          <p v-html="highlightMentions(message.message, allMentionableUsers)"></p>
                        </div>

                        <!-- Image attachments (below bubble text) -->
                        <div v-if="message.attachments?.length" class="message-attachments">
                          <div
                            v-for="(att, idx) in message.attachments"
                            :key="idx"
                            class="message-image-link"
                            @click="expandedImage = attSrc(att)"
                          >
                            <img :src="attSrc(att)" :alt="att.fileName" class="message-image" loading="lazy" />
                          </div>
                        </div>
                      </div>

                  </div>

                  <!-- Reactions below message -->
                  <div class="message-footer">
                    <!-- Reactions display -->
                    <div v-if="getGroupedReactions(message).length > 0" class="message-reactions">
                      <button
                        v-for="reaction in getGroupedReactions(message)"
                        :key="reaction.emoji"
                        class="reaction-badge"
                        :class="{ 'is-mine': reaction.hasUserReacted }"
                        @click="toggleReaction(message, reaction.emoji)"
                        :title="reaction.users.join(', ')"
                      >
                        {{ reaction.emoji }}
                        <span v-if="reaction.count > 1" class="reaction-count">{{ reaction.count }}</span>
                      </button>

                      <!-- Add reaction button when reactions exist -->
                      <button
                        class="add-reaction-btn"
                        @click="showEmojiPicker = message.id"
                        title="Add reaction"
                      >
                        <font-awesome-icon :icon="['fal', 'face-smile-plus']" />
                      </button>
                    </div>

                    <!-- Edited indicator -->
                    <span v-if="message.edited && !message.deleted" class="is-edited">edited</span>
                  </div>
                </div>

                  <!-- Avatar (right for current user) -->
                  <div v-if="message.userId === userAuthStore.user.id" class="message-avatar">
                    <div class="avatar-circle">
                      <BunnyImage
                        :fallback="getUserAvatar(message.userId)"
                        :alt="getUserFullName(message.userId, message.userName)"
                        img-class="avatar-image"
                      >
                        <template #placeholder>
                          <span>{{ getUserInitials(getUserFullName(message.userId, message.userName)) }}</span>
                        </template>
                      </BunnyImage>
                    </div>
                  </div>
                </div>
                </template>
              </template>
            </div>
          </div>

          <div class="composer-wrapper" v-if="isExisting">
            <!-- Mention suggestions dropdown -->
            <div class="mention-input-wrapper">
              <div v-if="showMentionSuggestions && mentionSuggestions.length > 0" class="mention-suggestions">
                <div
                  v-for="(user, index) in mentionSuggestions"
                  :key="user.id"
                  class="mention-suggestion"
                  :class="{ 'is-selected': index === selectedMentionIndex }"
                  @click="insertMention(user)"
                >
                  <div class="suggestion-avatar">
                    {{ getUserInitials(user.name || user.email) }}
                  </div>
                  <div class="suggestion-info">
                    <div class="suggestion-name">{{ user.name || 'Unnamed User' }}</div>
                    <div class="suggestion-email">{{ user.email }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="composer-card" :class="{ 'is-focused': editorFocused, 'is-editing': editingMessageId }">
              <!-- Floating toolbar — shows on focus -->
              <div class="formatting-toolbar" v-show="editorFocused">
                <button
                  class="format-btn"
                  :class="{ 'is-active': activeFormats.bold }"
                  title="Bold (Ctrl+B)"
                  @mousedown.prevent="applyFormat('bold')"
                  :disabled="sendingMessage"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fas', 'bold']" />
                  </span>
                </button>
                <button
                  class="format-btn"
                  :class="{ 'is-active': activeFormats.italic }"
                  title="Italic (Ctrl+I)"
                  @mousedown.prevent="applyFormat('italic')"
                  :disabled="sendingMessage"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fas', 'italic']" />
                  </span>
                </button>
                <button
                  class="format-btn"
                  :class="{ 'is-active': activeFormats.underline }"
                  title="Underline (Ctrl+U)"
                  @mousedown.prevent="applyFormat('underline')"
                  :disabled="sendingMessage"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fas', 'underline']" />
                  </span>
                </button>
                <span class="toolbar-divider"></span>
                <button
                  class="format-btn"
                  title="Attach image"
                  @mousedown.prevent="triggerImagePicker"
                  :disabled="sendingMessage || !!pendingAttachment"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fas', 'image']" />
                  </span>
                </button>
              </div>

              <!-- Attachment preview -->
              <div v-if="pendingAttachment" class="attachment-preview">
                <div class="attachment-thumb">
                  <img :src="pendingAttachment.objectUrl || pendingAttachment.url" :alt="pendingAttachment.fileName" />
                  <div v-if="uploadingImage" class="upload-overlay">
                    <BrandLoader :size="'lg'" />
                  </div>
                </div>
                <span class="attachment-name">{{ pendingAttachment.fileName }}</span>
                <button class="delete is-small" @click="removeAttachment" title="Remove attachment"></button>
              </div>

              <!-- Editor area -->
              <div class="composer-body">
                <div
                  ref="messageEditor"
                  class="message-editor"
                  data-placeholder="Type your message... (@ to mention users)"
                  @input="handleEditorInput"
                  @keydown="handleMessageKeydown"
                  @paste="handleEditorPaste"
                  @focus="handleEditorFocus"
                  @blur="handleEditorBlur"
                  :contenteditable="!sendingMessage && !!props.deliverable?.id"
                ></div>
                <!-- Circular send/save button -->
                <button
                  class="send-btn"
                  :class="{ 'is-editing': editingMessageId }"
                  @click="editingMessageId ? saveEditMessage() : sendMessage()"
                  :disabled="(editorIsEmpty && !pendingAttachment?.url) || sendingMessage || uploadingImage"
                  :title="editingMessageId ? 'Save edit' : 'Send message'"
                >
                  <font-awesome-icon
                    :icon="['fas', 'paper-plane-top']"
                    :active-icon="['fas', 'pen']"
                    :active="!!editingMessageId"
                  />
                </button>
              </div>
            </div>
            <div class="composer-hint-row">
              <p v-if="editingMessageId" class="composer-hint editing-hint">
                Editing message — Enter to save · Escape to cancel
                <a class="cancel-edit-link" @click.prevent="cancelEditMessage">Cancel</a>
              </p>
              <p v-else class="composer-hint">Enter to send · Shift+Enter for new line</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image lightbox overlay -->
    <Teleport to="body">
      <div v-if="expandedImage" class="image-lightbox" tabindex="0" @click="expandedImage = null" @keydown.esc.stop="expandedImage = null" ref="lightboxRef">
        <img :src="expandedImage" class="lightbox-image" @click.stop />
        <button class="delete is-large lightbox-close" @click="expandedImage = null"></button>
      </div>
    </Teleport>

    <!-- Hidden file input for image attachments -->
    <input
      ref="imageInputRef"
      type="file"
      accept="image/png,image/jpeg,image/gif,image/webp"
      style="display: none"
      @change="onFileInputChange"
    />
  </AppModal>

  <!-- Delete Confirmation Modal -->
  <AppModal
    v-model="showDeleteModal"
    title="Delete Deliverable"
    theme="danger"
    confirm-text="Delete"
    cancel-text="Cancel"
    :loading="isSaving"
    @confirm="deleteDeliverable"
    @cancel="showDeleteModal = false"
  >
    <p>
      Are you sure you want to delete "{{ form.title }}"? This action cannot be undone.
    </p>
  </AppModal>

  <!-- Video Preview Modal -->
  <VideoPreviewModal
    :show="showVideoPreviewModal"
    :asset="previewAsset"
    :show-navigation="previewAssetList.length > 1"
    :can-navigate-prev="canNavigatePrev"
    :can-navigate-next="canNavigateNext"
    @close="closeVideoPreviewModal"
    @navigate="handlePreviewNavigate"
  />

  <!-- Dependency Override Modal -->
  <DependencyOverrideModal
    :is-active="showDependencyOverride"
    :blockers="dependencyBlockers"
    :target-stage="dependencyTargetStage"
    @close="closeDependencyOverride"
    @override="handleDependencyOverride"
  />
</template>

<script setup>
import BrandLoader from '@/components/global/BrandLoader.vue';
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { refreshBunnyUrl } from '@/services/bunnyUrlService';
import { useAdminProjectsStore } from '@/stores/adminProjectsStore';
import { useAdminDeliverablesStore } from '@/stores/adminDeliverablesStore';
import { useAdminCalendarStore } from '@/stores/adminCalendarStore';
import { useUserAuthStore } from '@/stores/userAuthStore';
import AppModal from '@/components/global/AppModal.vue';
import SearchableSelect from '@/components/common/SearchableSelect.vue';
import UserAvatarSelect from '@/components/common/UserAvatarSelect.vue';
import BunnyImage from '@/components/common/BunnyImage.vue';
import ProjectBrief from '@/components/common/ProjectBrief.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import DeliverableActivityTimeline from './DeliverableActivityTimeline.vue';
import DeliverableActionButtons from './DeliverableActionButtons.vue';
import DeliverableReviewTab from './DeliverableReviewTab.vue';
import DeliverableStatusBar from './DeliverableStatusBar.vue';
import MediaCard from '@/components/common/MediaCard.vue';
import VideoPreviewModal from '@/components/common/VideoPreviewModal.vue';
import { useClientAssetsStore } from '@/stores/clientAssetsStore';
import {
  getDeliverableTypeOptions,
  getDeliverableStatusOptions,
  getDeliverableStageOptions,
  ASPECT_RATIOS,
  DELIVERABLE_STATUSES,
  DELIVERABLE_STAGES,
  getDeliverableTypeIcon,
  getDeliverableTypeColor,
  getDeliverableTypeLabel,
  getDeliverableStatusIcon,
  getDeliverableStatusClassName,
  getDeliverableStatusLabel,
  getDeliverableStageIcon,
  getDeliverableStageClassName,
  getDeliverableStageLabel,
  DELIVERABLE_WEIGHTS,
  getDeliverableWeightLabel
} from '@/utils/deliverableUtils';
import { getEventTypeIcon, getEventTypeColor } from '@/utils/calendarEventTypes';
import { logger } from '@/services/loggerService';
import { toastBridge as toast } from '@/services/toastBridge';
import { useExtractAudio } from '@/composables/useExtractAudio';
import { dependencyService } from '@/services/dependencyService';
import DependencyOverrideModal from '@/components/admin/deliverable/DependencyOverrideModal.vue';
import DeliverableDependencies from '@/components/admin/deliverable/DeliverableDependencies.vue';
import { discussionService } from '@/services/discussionService';
import { userService } from '@/services/userService';
import { deliverableService } from '@/services/deliverableService';
import { Timestamp } from 'firebase/firestore';
import { PROJECT_STATUS } from '@/utils/projectUtils';
import { buildAssetDownloadFilename } from '@/utils/assetUtils';
import { findUserById, userDisplayName } from '@/utils/userUtils';

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  deliverable: {
    type: Object,
    default: null
  },
  project: {
    type: Object,
    default: null
  },
  company: {
    type: Object,
    default: null
  },
  productionEvents: {
    type: Array,
    default: () => []
  }
});

// Emits
const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

// Stores
const projectsStore = useAdminProjectsStore();
const deliverablesStore = useAdminDeliverablesStore();
const calendarStore = useAdminCalendarStore();
const userAuthStore = useUserAuthStore();
const clientAssetsStore = useClientAssetsStore();

// Router
const router = useRouter();

// State
const isSaving = ref(false);
const showDeleteModal = ref(false);
const showDependencyOverride = ref(false);
const dependencyBlockers = ref([]);
const dependencyTargetStage = ref('');
const errors = ref({});
const activeTab = ref('details');
// Tabs that hide the discussion column and take the full modal width — both are
// dense, wide readouts that the 50/50 split squeezes into unreadable wrapping.
const isFullWidthTab = computed(() => ['review', 'activity', 'assets'].includes(activeTab.value));
const reviewTabActivated = ref(false);
const isEditMode = ref(false);
const isEditingDueDate = ref(false);
const tempDueDate = ref(null);
const isEditingStage = ref(false);
const tempStage = ref(null);
const isEditingStatus = ref(false);
const tempStatus = ref(null);
const originalAssignedUserId = ref('');
const originalPrimaryEditorId = ref('');

// Discussion state
const messages = ref([]);
const loadingMessages = ref(false);
const sendingMessage = ref(false);
const chatMessages = ref(null);
const editingMessageId = ref(null);
const editingMessageText = ref('');
const showEmojiPicker = ref(null);
const discussionUnsubscribe = ref(null);
const showMentionSuggestions = ref(false);
const mentionSearchTerm = ref('');
const mentionSuggestions = ref([]);
const allMentionableUsers = ref([]); // Cache all users for mention resolution
const selectedMentionIndex = ref(0);
const messageEditor = ref(null); // Contenteditable div ref
const editorIsEmpty = ref(true); // Track if editor has content
const editorFocused = ref(false); // Track editor focus for toolbar visibility
const activeFormats = ref({ bold: false, italic: false, underline: false });
let editorBlurTimeout = null;
const deleteConfirmMessageId = ref(null);

// Image attachment state
const pendingAttachment = ref(null);
const uploadingImage = ref(false);
const imageInputRef = ref(null);
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
const expandedImage = ref(null);

// Attachment `url` is a 30-day signed Bunny URL that eventually 403s. Messages
// also carry `storagePath`, so re-sign from that. Discussion attachments render
// inside a nested v-for (messages → attachments), which rules out a per-item
// composable — so resolve every path into this map and read it via attSrc().
// refreshBunnyUrl caches per path, so repeat renders don't re-mint.
const resolvedAttachmentUrls = ref({});

const attSrc = (att) => (att?.storagePath && resolvedAttachmentUrls.value[att.storagePath]) || att?.url;

watch(
  messages,
  async (list) => {
    const paths = (list || [])
      .flatMap((m) => m.attachments || [])
      .map((a) => a.storagePath)
      .filter((p) => p && !resolvedAttachmentUrls.value[p]);
    if (!paths.length) return;
    const signed = await Promise.all(
      [...new Set(paths)].map(async (p) => [p, await refreshBunnyUrl({ path: p })])
    );
    resolvedAttachmentUrls.value = {
      ...resolvedAttachmentUrls.value,
      ...Object.fromEntries(signed.filter(([, url]) => url)),
    };
  },
  { immediate: true, deep: true }
);
const lightboxRef = ref(null);

// Clone and detach state
const isCloning = ref(false);
const loadingOriginalBrief = ref(false);
const originalBriefData = ref(null);
const originalProjectName = ref(null);
const originalProjectId = ref(null);
const originalDeliverableInfo = ref(null);
const originalDeliverableDeleted = ref(false);
const originalBriefError = ref(null);
const loadingOriginal = ref(false);

// Asset preview state
const showVideoPreviewModal = ref(false);
const previewAsset = ref(null);
const previewAssetList = ref([]);
const previewAssetIndex = ref(0);

// Asset download state
const isDownloadingAll = ref(false);
const downloadingAssetIds = ref([]);
// Final Assets tab: clientAssetsStore is a live listener, so the new MP3 appears on its own.
const { extractAudio, isExtracting, canExtractAudio, hasExtractedAudio } = useExtractAudio();

// User data cache for consistent display across all users
const userDataCache = ref(new Map());

// Fetch user data directly from Firestore
async function fetchUserFromFirestore(userId) {
  if (!userId) return null;

  // Check cache first
  if (userDataCache.value.has(userId)) {
    return userDataCache.value.get(userId);
  }

  // Handle special system users (imported from external sources)
  if (userId === 'monday-import') {
    const systemUser = {
      id: userId,
      firstName: 'Monday',
      lastName: 'Import',
      name: 'Monday Import',
      email: '',
      avatar: '',
      displayName: 'Monday Import',
      isSystemUser: true
    };
    userDataCache.value.set(userId, systemUser);
    return systemUser;
  }

  try {
    // Fetch user using userService
    const user = await userService.getUser(userId);

    if (user) {
      const userData = {
        id: userId,
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim(),
        email: user.profile?.email || user.email || '',
        avatar: user.profile?.avatar || user.avatar || user.photoURL || '',
        displayName: user.displayName || user.name || ''
      };

      // Cache the data
      userDataCache.value.set(userId, userData);
      logger.info('[DeliverableModal] User data fetched', { userId, name: userData.name });
      return userData;
    }
  } catch (error) {
    logger.error('[DeliverableModal] Failed to fetch user', { userId, error: error.message });
  }

  return null;
}

// Form data
const form = ref({
  title: '',
  type: 'standard',
  status: DELIVERABLE_STATUSES.NOT_STARTED,
  stage: DELIVERABLE_STAGES.NOT_READY,
  aspectRatio: '16:9',
  runtime: '',
  resolution: '1080p',
  weight: null,
  description: '',
  dueDate: null,
  productionEventIds: [],
  dependencies: [],
  linkedDeliverableIds: [],
  isPrimaryEpisodeDeliverable: false,
  assignedUserId: '',
  primaryEditorId: ''
});

// Computed
const projectBrief = computed(() => {
  // Check for 'breif' (typo in data) field - this is how it's stored.
  // Read from liveProject so freshly-hydrated brief data flows through.
  const briefData = liveProject.value?.breif;

  if (!briefData) return [];

  let processedBrief = [];

  // Extract sections array from brief object if it exists
  if (briefData.sections && Array.isArray(briefData.sections)) {
    processedBrief = briefData.sections;
  } else if (Array.isArray(briefData)) {
    processedBrief = briefData;
  } else if (typeof briefData === 'object') {
    // Check if it has numbered keys (like converted from Firestore)
    const keys = Object.keys(briefData);
    if (keys.every(key => !isNaN(parseInt(key)))) {
      // Convert numbered object to array
      processedBrief = keys.sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => briefData[key]);
    } else {
      // Try to extract sections or return empty
      return [];
    }
  }

  // Ensure each section has the required structure (name and fields).
  // Keep asset-selection sections too: they carry selectedAssetIds (clips) but
  // intentionally have no `fields`, so the bare fields check would drop them.
  const validBrief = processedBrief.filter(section => {
    return section && typeof section === 'object' &&
           'name' in section &&
           ('fields' in section || section.type === 'asset-selection');
  });

  return validBrief;
});

// Add closing animation state
const isClosing = ref(false);

// Minimize modal function
const minimizeModal = () => {
  if (!isExisting.value || !props.deliverable) {
    logger.warn('[DeliverableModal] Cannot minimize non-existing deliverable');
    return;
  }

  // Add deliverable to minimized store
  deliverablesStore.minimizeDeliverable({
    id: props.deliverable.id,
    title: props.deliverable.title || form.value.title,
    type: props.deliverable.type || form.value.type,
    status: props.deliverable.status || form.value.status,
    stage: props.deliverable.stage || form.value.stage,
    project: props.project,
    company: props.company,
    assignedUserId: props.deliverable.assignedUserId || form.value.assignedUserId,
    dueDate: props.deliverable.dueDate || form.value.dueDate
  });

  // Close modal with animation
  isClosing.value = true;
  setTimeout(() => {
    emit('update:modelValue', false);
    isClosing.value = false;
  }, 300);

  logger.info('[DeliverableModal] Deliverable minimized', {
    deliverableId: props.deliverable.id,
    title: props.deliverable.title
  });
};

// Close modal function
const closeModal = () => {
  isClosing.value = true;
  removeDeliverableFromUrl();
  setTimeout(() => {
    emit('update:modelValue', false);
    isClosing.value = false;
  }, 300);
};

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => {
    if (!value && props.modelValue) {
      // Modal is closing, trigger animation
      isClosing.value = true;
      removeDeliverableFromUrl();
      setTimeout(() => {
        emit('update:modelValue', false);
        isClosing.value = false;
      }, 300); // Match animation duration
    } else {
      emit('update:modelValue', value);
    }
  }
});

const isExisting = computed(() => !!props.deliverable?.id);
const deliverableProjectId = computed(() => props.project?.id || props.deliverable?.projectId || null);

// Callers may pass a stub project (e.g. `{ id }` from `getProjectById` miss, or
// an episode filtered out of the main init query). Resolve the live, full
// project from the store so brief/episode/subscribers panels render correctly.
// Reads `items` directly rather than via getProjectById, which has a non-reactive
// Map cache that can mask reactive updates after writes (e.g. adding a subscriber).
const liveProject = computed(() => {
  const id = deliverableProjectId.value;
  if (!id) return props.project || null;
  const fromStore = projectsStore.items?.[id];
  return (fromStore && fromStore.id) ? fromStore : (props.project || null);
});

// Live deliverable data from store - updates in real-time via Firestore onSnapshot
const liveDeliverable = computed(() => {
  if (!props.deliverable?.id) return props.deliverable;
  // Get the latest data from the store (updated via real-time listener)
  return deliverablesStore.getDeliverableById(props.deliverable.id) || props.deliverable;
});

// Group messages by day for display with dividers
const messagesGroupedByDay = computed(() => {
  if (!messages.value || messages.value.length === 0) return [];

  const groups = [];
  let currentGroup = null;

  messages.value.forEach((message) => {
    const messageDate = message.timestamp?.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
    const dateKey = messageDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    if (!currentGroup || currentGroup.dateKey !== dateKey) {
      currentGroup = {
        dateKey,
        dateDisplay: getDateDisplay(messageDate),
        messages: []
      };
      groups.push(currentGroup);
    }

    currentGroup.messages.push(message);
  });

  return groups;
});

const modalTitle = computed(() => {
  if (!isExisting.value) return 'Add Deliverable';
  if (isEditMode.value) return 'Edit Deliverable';
  return form.value.title || 'Deliverable Details';
});

const typeOptions = computed(() => getDeliverableTypeOptions());
const statusOptions = computed(() => getDeliverableStatusOptions());
const stageOptions = computed(() => getDeliverableStageOptions());
const aspectRatioOptions = computed(() => ASPECT_RATIOS);
const weightOptions = computed(() => DELIVERABLE_WEIGHTS);

// Check if status editing is disabled (when stage is "not_ready")
const isStatusEditingDisabled = computed(() => {
  const stage = form.value.stage || 'not_ready';
  return stage === 'not_ready';
});

const availableEvents = computed(() => {
  // Get all calendar events for this company (not just project)
  const allEvents = calendarStore.allEvents || (calendarStore.items ? Object.values(calendarStore.items) : []);

  // Get company ID from props or deliverable
  const companyId = props.company?.id || props.deliverable?.companyId;
  const projectId = props.project?.id || props.deliverable?.projectId;

  if (!companyId) return [];

  return allEvents
    .filter(event => event && event.companyId === companyId)
    .map(event => ({
      id: event.id,
      title: event.title,
      date: event.date,
      type: event.type,
      projectId: event.projectId,
      projectName: event.projectName || 'Unknown Project',
      isCurrentProject: projectId ? event.projectId === projectId : false,
      typeIcon: getEventTypeIcon(event.type),
      color: getEventTypeColor(event.type)
    }))
    .sort((a, b) => {
      // Sort by project (current project first) then by date
      if (a.isCurrentProject && !b.isCurrentProject) return -1;
      if (!a.isCurrentProject && b.isCurrentProject) return 1;
      const dateA = a.date?.toMillis ? a.date.toMillis() : 0;
      const dateB = b.date?.toMillis ? b.date.toMillis() : 0;
      return dateA - dateB;
    });
});

const selectedEvents = computed(() => {
  if (!form.value.productionEventIds?.length) return [];
  return availableEvents.value.filter(event => 
    form.value.productionEventIds.includes(event.id)
  );
});

const isValid = computed(() => {
  return form.value.title?.trim() && 
         form.value.type && 
         form.value.status;
});

const assignedUser = computed(() =>
  findUserById(userAuthStore.allUsers?.users, form.value.assignedUserId)
);

const primaryEditor = computed(() =>
  findUserById(userAuthStore.allUsers?.users, form.value.primaryEditorId)
);

// Check if current deliverable is a clone
const isClonedDeliverable = computed(() => {
  return !!props.deliverable?.clonedFromDeliverableId;
});

// Get effective brief (original project's brief for clones, or passed prop)
const effectiveBrief = computed(() => {
  if (isClonedDeliverable.value && originalBriefData.value) {
    // Process the dynamically fetched brief
    const briefData = originalBriefData.value;
    if (!briefData) return [];

    let processedBrief = [];
    if (briefData.sections && Array.isArray(briefData.sections)) {
      processedBrief = briefData.sections;
    } else if (Array.isArray(briefData)) {
      processedBrief = briefData;
    } else if (typeof briefData === 'object') {
      const keys = Object.keys(briefData);
      if (keys.every(key => !isNaN(parseInt(key)))) {
        processedBrief = keys.sort((a, b) => parseInt(a) - parseInt(b))
          .map(key => briefData[key]);
      }
    }

    return processedBrief.filter(section =>
      section && typeof section === 'object' && 'name' in section &&
      ('fields' in section || section.type === 'asset-selection')
    );
  }
  return projectBrief.value;
});

const effectiveProjectStatus = computed(() => {
  if (isClonedDeliverable.value) {
    return PROJECT_STATUS.DRAFT;
  }
  return props.project?.status || PROJECT_STATUS.DRAFT;
});

const effectiveProjectId = computed(() => {
  if (isClonedDeliverable.value) {
    return originalProjectId.value;
  }
  return props.project?.id;
});

// Get assets linked to this deliverable
const deliverableAssets = computed(() => {
  if (!props.deliverable?.id) return [];
  return clientAssetsStore.items.filter(
    asset => asset.deliverableId === props.deliverable.id
  );
});

// --- Final Assets pagination ---
const ASSET_PAGE_SIZES = [12, 24, 48];
const assetsPerPage = ref(ASSET_PAGE_SIZES[0]);
const assetPage = ref(1);
const assetTotalPages = computed(() => Math.max(1, Math.ceil(deliverableAssets.value.length / assetsPerPage.value)));
const assetPageStart = computed(() => (assetPage.value - 1) * assetsPerPage.value);
const pagedDeliverableAssets = computed(() =>
  deliverableAssets.value.slice(assetPageStart.value, assetPageStart.value + assetsPerPage.value)
);
// Up to 5 page links, windowed around the current page
const assetPageNumbers = computed(() => {
  const total = assetTotalPages.value;
  const start = Math.max(1, Math.min(assetPage.value - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});
// Back to page 1 on a new deliverable or page size; clamp if assets disappear
watch([() => props.deliverable?.id, assetsPerPage], () => { assetPage.value = 1; });
watch(assetTotalPages, (total) => { if (assetPage.value > total) assetPage.value = total; });

// One source for the pill row and the ←/→ keyboard nav. This used to be a list
// of keys with a comment noting it "mirrors the v-if conditions on the tab <li>
// elements above" — two places to keep in step. Now the template renders from
// this, so they cannot drift.
const visibleTabs = computed(() => {
  if (!isExisting.value) return [];
  const tabs = [{ key: 'details', label: 'Details', icon: 'info-circle' }];
  if (liveProject.value?.breif || isClonedDeliverable.value) {
    tabs.push({ key: 'brief', label: 'Project Brief', icon: 'file-lines' });
  }
  tabs.push({ key: 'review', label: 'Review', icon: 'list-check' });
  if (deliverableAssets.value.length > 0) {
    tabs.push({ key: 'assets', label: 'Final Assets', icon: 'photo-film' });
  }
  tabs.push({ key: 'activity', label: 'Activity', icon: 'history' });
  return tabs;
});

const visibleTabKeys = computed(() => visibleTabs.value.map((t) => t.key));

// The Review tab lazily mounts its heavy child, so selection has a side effect.
const selectTab = (key) => {
  activeTab.value = key;
  if (key === 'review') reviewTabActivated.value = true;
};

const handleTabArrowNav = (e) => {
  // Option/Alt + ←/→ only
  if (
    (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') ||
    !e.altKey || e.metaKey || e.ctrlKey || e.shiftKey
  ) {
    return;
  }
  if (!isExisting.value) return;
  // Overlays handle (or should own) their own keys
  if (expandedImage.value || previewAsset.value) return;
  // A stacked modal (confirm dialog, etc.) is open on top of this one
  if (document.querySelectorAll('.modal.is-active').length > 1) return;

  // Don't hijack typing, native controls, or the video player's seek keys
  const target = e.target;
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable ||
      target.closest?.('.video-js'))
  ) {
    return;
  }

  const tabs = visibleTabKeys.value;
  const current = tabs.indexOf(activeTab.value);
  if (current === -1) return;
  const next = current + (e.key === 'ArrowRight' ? 1 : -1);
  if (next < 0 || next >= tabs.length) return;

  e.preventDefault();
  selectTab(tabs[next]);
};

onMounted(() => {
  document.addEventListener('keydown', handleTabArrowNav);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleTabArrowNav);
});

// Computed for navigation state
const canNavigatePrev = computed(() => previewAssetIndex.value > 0);
const canNavigateNext = computed(() => previewAssetIndex.value < previewAssetList.value.length - 1);

// Asset preview handlers
const handleAssetPreview = (asset) => {
  logger.debug('[DeliverableModal] Asset preview clicked', { assetId: asset.id });

  // Set up preview list with all assets for navigation
  previewAssetList.value = deliverableAssets.value;
  previewAssetIndex.value = deliverableAssets.value.findIndex(a => a.id === asset.id);
  previewAsset.value = asset;
  showVideoPreviewModal.value = true;
};

const closeVideoPreviewModal = () => {
  showVideoPreviewModal.value = false;
  previewAsset.value = null;
};

const handlePreviewNavigate = (direction) => {
  // direction is 'prev' or 'next'
  const delta = direction === 'prev' ? -1 : 1;
  const newIndex = previewAssetIndex.value + delta;
  if (newIndex >= 0 && newIndex < previewAssetList.value.length) {
    previewAssetIndex.value = newIndex;
    previewAsset.value = previewAssetList.value[newIndex];
  }
};

// Download a single asset
const downloadSingleAsset = async (asset) => {
  if (downloadingAssetIds.value.includes(asset.id)) return;

  downloadingAssetIds.value.push(asset.id);

  try {
    logger.info('[DeliverableModal] Downloading asset', { assetId: asset.id, name: asset.name });

    // Use the 'download' intent so Bunny mints an attachment-disposition URL
    // (download=true&filename=). getAssetUrl's default 'playback' intent
    // returns a streaming URL that just opens in a new tab for Bunny assets.
    const source = await clientAssetsStore.getAssetSource(asset, 'download');
    const url = source?.url;
    if (!url) {
      throw new Error('Could not get asset URL');
    }

    const filename = buildAssetDownloadFilename(asset);

    // Create download link. No target="_blank": it fights the download
    // attribute and, for cross-origin Bunny URLs, forces a new tab instead.
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);

    toast.success(`Downloading ${filename}`);
  } catch (error) {
    logger.error('[DeliverableModal] Failed to download asset', { assetId: asset.id, error: error.message });
    toast.error('Failed to download asset');
  } finally {
    downloadingAssetIds.value = downloadingAssetIds.value.filter(id => id !== asset.id);
  }
};

// Download all assets
const downloadAllAssets = async () => {
  if (isDownloadingAll.value) return;

  isDownloadingAll.value = true;

  try {
    logger.info('[DeliverableModal] Downloading all assets', { count: deliverableAssets.value.length });

    let successCount = 0;

    for (const asset of deliverableAssets.value) {
      try {
        const source = await clientAssetsStore.getAssetSource(asset, 'download');
        const url = source?.url;
        if (!url) continue;

        const filename = buildAssetDownloadFilename(asset);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        successCount++;

        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        logger.error('[DeliverableModal] Failed to download asset in bulk', { assetId: asset.id, error: error.message });
      }
    }

    if (successCount > 0) {
      toast.success(`Started downloading ${successCount} file${successCount > 1 ? 's' : ''}`);
    }
  } catch (error) {
    logger.error('[DeliverableModal] Failed to download all assets', { error: error.message });
    toast.error('Failed to download assets');
  } finally {
    isDownloadingAll.value = false;
  }
};

// Watch for deliverable prop changes
// Watch for modal open/close
watch(isOpen, (newValue) => {
  if (newValue && messages.value.length > 0) {
    // Scroll to bottom when modal opens and messages exist
    scrollToBottomOfChat();
  }
});

// Auto-focus lightbox so it captures Escape key
watch(expandedImage, (val) => {
  if (val) {
    nextTick(() => lightboxRef.value?.focus());
  }
});

// Watch for messages to load and scroll to bottom
watch(messages, () => {
  if (messages.value.length > 0) {
    scrollToBottomOfChat();
    // Re-observe messages when new ones arrive
    nextTick(() => {
      observeMessages();
    });
  }
});

// Watch for initial deliverable prop to set up form and load messages
watch(() => props.deliverable, (newDeliverable) => {
  if (newDeliverable) {
    // Populate form with existing deliverable data
    form.value = {
      title: newDeliverable.title || '',
      type: newDeliverable.type || 'standard',
      status: newDeliverable.status || DELIVERABLE_STATUSES.NOT_STARTED,
      stage: newDeliverable.stage || DELIVERABLE_STAGES.NOT_READY,
      aspectRatio: newDeliverable.aspectRatio || '16:9',
      runtime: newDeliverable.runtime || '',
      resolution: newDeliverable.resolution || '1080p',
      weight: newDeliverable.weight ?? null,
      description: newDeliverable.description || '',
      dueDate: newDeliverable.dueDate?.toDate ? newDeliverable.dueDate.toDate() : newDeliverable.dueDate,
      productionEventIds: newDeliverable.productionEventIds || [],
      dependencies: newDeliverable.dependencies || [],
      linkedDeliverableIds: newDeliverable.linkedDeliverableIds || [],
      isPrimaryEpisodeDeliverable: newDeliverable.isPrimaryEpisodeDeliverable || false,
      assignedUserId: newDeliverable.assignedUserId || '',
      primaryEditorId: newDeliverable.primaryEditorId || ''
    };
    // Store original values for inline editing
    originalAssignedUserId.value = newDeliverable.assignedUserId || '';
    originalPrimaryEditorId.value = newDeliverable.primaryEditorId || '';
    tempDueDate.value = form.value.dueDate;
    tempStage.value = form.value.stage;
    tempStatus.value = form.value.status;
    // Start in view mode for existing deliverables
    isEditMode.value = false;
    isEditingDueDate.value = false;
    isEditingStage.value = false;
    isEditingStatus.value = false;
    activeTab.value = 'details';
    reviewTabActivated.value = false;

    // Load discussion messages for existing deliverables
    if (newDeliverable.id) {
      loadMessages();
    }

    // Hydrate the project into the store if the caller passed a stub (e.g.
    // episodes filtered out of the init query, or `getProjectById` misses).
    // Once fetched, liveProject reactively picks it up so brief / episode /
    // subscribers panels render with the full document.
    const hydrateId = newDeliverable.projectId || props.project?.id;
    if (hydrateId && projectsStore.fetchProjectById) {
      projectsStore.fetchProjectById(hydrateId).catch((error) => {
        logger.warn('[DeliverableModal] Failed to hydrate project', {
          projectId: hydrateId,
          error: error.message
        });
      });
    }

    // Load original project brief for cloned deliverables
    if (newDeliverable.clonedFromDeliverableId) {
      loadOriginalProjectBrief();
    } else {
      resetCloneState();
    }
  } else {
    // Reset form for new deliverable
    resetForm();
    // New deliverables start in edit mode
    isEditMode.value = true;
    activeTab.value = 'details';
    reviewTabActivated.value = false;

    // Clear messages for new deliverables
    messages.value = [];

    // Reset clone state
    resetCloneState();
  }
}, { immediate: true });

// Watch for live updates from the store (real-time Firestore changes)
// Only update non-editing fields to avoid overwriting user input
watch(liveDeliverable, (newData, oldData) => {
  // Skip if no data, not in view mode, or same reference
  if (!newData || !oldData || newData === oldData) return;
  // Skip if user is actively editing
  if (isEditMode.value) return;

  // Update view-mode display values when store data changes
  // This handles updates from automation rules, other users, etc.
  if (!isEditingStage.value && newData.stage !== oldData.stage) {
    form.value.stage = newData.stage;
    tempStage.value = newData.stage;
    logger.info('[DeliverableModal] Live update: stage changed', {
      from: oldData.stage,
      to: newData.stage
    });
  }
  if (!isEditingStatus.value && newData.status !== oldData.status) {
    form.value.status = newData.status;
    tempStatus.value = newData.status;
    logger.info('[DeliverableModal] Live update: status changed', {
      from: oldData.status,
      to: newData.status
    });
  }
  if (!isEditingDueDate.value && newData.dueDate !== oldData.dueDate) {
    const newDueDate = newData.dueDate?.toDate ? newData.dueDate.toDate() : newData.dueDate;
    form.value.dueDate = newDueDate;
    tempDueDate.value = newDueDate;
  }
  if (newData.assignedUserId !== oldData.assignedUserId) {
    form.value.assignedUserId = newData.assignedUserId || '';
    originalAssignedUserId.value = newData.assignedUserId || '';
  }
  if (newData.primaryEditorId !== oldData.primaryEditorId) {
    form.value.primaryEditorId = newData.primaryEditorId || '';
    originalPrimaryEditorId.value = newData.primaryEditorId || '';
  }
  if (newData.title !== oldData.title) {
    form.value.title = newData.title || '';
  }
  if (newData.description !== oldData.description) {
    form.value.description = newData.description || '';
  }
  if (newData.type !== oldData.type) {
    form.value.type = newData.type || 'standard';
  }
}, { deep: true });

// Methods
function resetForm() {
  // Auto-select all events from the current project
  const projectEventIds = availableEvents.value
    .filter(event => event.isCurrentProject)
    .map(event => event.id);

  form.value = {
    title: '',
    type: 'standard',
    status: DELIVERABLE_STATUSES.NOT_STARTED,
    stage: DELIVERABLE_STAGES.NOT_READY,
    aspectRatio: '16:9',
    runtime: '',
    resolution: '1080p',
    weight: null,
    description: '',
    dueDate: null,
    productionEventIds: projectEventIds,
    dependencies: [],
    linkedDeliverableIds: [],
    isPrimaryEpisodeDeliverable: false,
    assignedUserId: ''
  };
  errors.value = {};
}

function validateForm() {
  errors.value = {};
  
  if (!form.value.title?.trim()) {
    errors.value.title = 'Title is required';
  }
  
  return Object.keys(errors.value).length === 0;
}

function formatRuntime(event) {
  let value = event.target.value.replace(/[^\d:]/g, '');
  
  // Auto-format as user types
  if (value.length === 2 && !value.includes(':')) {
    value = value + ':';
  }
  
  // Limit to mm:ss format
  const parts = value.split(':');
  if (parts.length > 2) {
    value = parts[0] + ':' + parts[1];
  }
  
  // Limit minutes to 99 and seconds to 59
  if (parts.length === 2) {
    const minutes = parts[0].substring(0, 2);
    const seconds = parts[1].substring(0, 2);
    
    if (seconds && parseInt(seconds) > 59) {
      value = minutes + ':59';
    } else {
      value = minutes + (seconds ? ':' + seconds : ':');
    }
  }
  
  form.value.runtime = value;
}

// Removed handleConfirm as modal footer is no longer used

// Helper to safely convert date to Timestamp
function safeTimestampFromDate(dateValue) {
  if (!dateValue) return null;
  try {
    const date = new Date(dateValue);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      logger.warn('[DeliverableModal] Invalid date value', { dateValue });
      return null;
    }
    return Timestamp.fromDate(date);
  } catch (error) {
    logger.warn('[DeliverableModal] Error converting date to Timestamp', {
      dateValue,
      error: error.message
    });
    return null;
  }
}

async function saveDeliverable() {
  if (!validateForm()) return;

  // Get project/company IDs from props or existing deliverable
  const projectId = props.project?.id || props.deliverable?.projectId || null;
  const companyId = props.company?.id || props.deliverable?.companyId;

  if (!companyId) {
    logger.error('[DeliverableModal] Cannot save deliverable - no company ID');
    toast.error('Unable to save: Company information is missing');
    return;
  }

  try {
    isSaving.value = true;

    const deliverableData = {
      ...form.value,
      projectId,
      companyId,
      dueDate: safeTimestampFromDate(form.value.dueDate)
    };

    // Don't send status when stage is not_ready to avoid validation conflict
    if (deliverableData.stage === 'not_ready') {
      delete deliverableData.status;
    }
    
    let result;
    if (isExisting.value) {
      // Update existing deliverable
      result = await projectsStore.updateDeliverable(props.deliverable.id, deliverableData, false);
      logger.info('[DeliverableModal] Deliverable updated', {
        deliverableId: props.deliverable.id
      });
    } else {
      // Create new deliverable
      result = await projectsStore.addDeliverable(deliverableData);
      logger.info('[DeliverableModal] Deliverable created', {
        deliverableId: result.id
      });
    }
    
    emit('saved', { id: result.id || props.deliverable.id, ...deliverableData });
    
    // If editing existing, just exit edit mode instead of closing
    if (isExisting.value) {
      isEditMode.value = false;
      // Update form with saved data
      Object.assign(form.value, deliverableData);
      toast.success('Deliverable updated successfully');
    } else {
      // Only close modal for new deliverables
      toast.success('Deliverable created successfully');
      isOpen.value = false;
      resetForm();
    }
  } catch (error) {
    logger.error('[DeliverableModal] Failed to save deliverable', {
      error: error.message
    });
  } finally {
    isSaving.value = false;
  }
}

function confirmDelete() {
  showDeleteModal.value = true;
}

function viewFullProject() {
  // Don't trust the `project` prop alone: openers resolve it from
  // adminProjectsStore, whose getProjectById returns {} on a miss, and episode
  // or archived projects aren't in the init set. The deliverable's own
  // projectId is always there.
  const projectId = props.project?.id || props.deliverable?.projectId;

  if (!projectId) {
    logger.warn('[DeliverableModal] View Full Project: no project id on deliverable', {
      deliverableId: props.deliverable?.id
    });
    return;
  }

  router.push({ name: 'admin-project-edit', params: { project: projectId } });
}

async function deleteDeliverable() {
  try {
    isSaving.value = true;
    await projectsStore.deleteDeliverable(props.deliverable.id);

    logger.info('[DeliverableModal] Deliverable deleted', {
      deliverableId: props.deliverable.id
    });

    emit('deleted', props.deliverable.id);
    showDeleteModal.value = false;
    isOpen.value = false;
    resetForm();
  } catch (error) {
    logger.error('[DeliverableModal] Failed to delete deliverable', {
      error: error.message
    });
  } finally {
    isSaving.value = false;
  }
}

// Clone and detach the current deliverable
async function handleCloneAndDetach() {
  if (!props.deliverable?.id) return;

  try {
    isCloning.value = true;

    // Capture company reference before any state changes
    const company = props.company;

    const result = await deliverablesStore.cloneAndDetachDeliverable(props.deliverable.id);

    if (result.success) {
      logger.info('[DeliverableModal] Deliverable cloned and detached', {
        originalId: props.deliverable.id,
        newId: result.id
      });

      // Minimize the original deliverable instead of closing
      minimizeModal();

      // Open the new cloned deliverable after the minimize animation
      setTimeout(async () => {
        const clonedDeliverable = await deliverableService.getDeliverable(result.id);
        if (clonedDeliverable) {
          deliverablesStore.openDeliverableModal(
            clonedDeliverable,
            null, // No project for detached
            company // Keep same company (captured before minimize)
          );
        }
        isCloning.value = false;
      }, 350);
    } else {
      isCloning.value = false;
    }
  } catch (error) {
    logger.error('[DeliverableModal] Failed to clone and detach', { error: error.message });
    isCloning.value = false;
  }
}

// Handle action button execution
function handleActionExecuted(event) {
  logger.info('[DeliverableModal] Action executed', event);
  // The deliverable will be updated via the real-time listener
  // Just show a success message - the store already gets updated
  toast.success('Action completed successfully');
}

// View the original deliverable (opens in new modal)
async function viewOriginalDeliverable() {
  if (!props.deliverable?.clonedFromDeliverableId) return;

  try {
    loadingOriginal.value = true;

    // Get original deliverable
    const original = await deliverableService.getDeliverable(props.deliverable.clonedFromDeliverableId);

    if (!original) {
      originalDeliverableDeleted.value = true;
      loadingOriginal.value = false;
      return;
    }

    // Fetch project for original if it has one
    let project = null;
    if (original.projectId) {
      project = projectsStore.getProjectById(original.projectId);
    }

    // Capture company reference BEFORE minimizing (props.company will become null after modal closes)
    const company = props.company;

    // Minimize current modal first
    minimizeModal();

    // Open original in modal after the minimize animation completes
    setTimeout(() => {
      deliverablesStore.openDeliverableModal(original, project, company);
      loadingOriginal.value = false;
    }, 350);

  } catch (error) {
    logger.error('[DeliverableModal] Failed to open original deliverable', { error: error.message });
    loadingOriginal.value = false;
  }
}

// Load original project brief for cloned deliverables
async function loadOriginalProjectBrief() {
  if (!isClonedDeliverable.value || !props.deliverable?.id) return;

  try {
    loadingOriginalBrief.value = true;
    originalBriefError.value = null;

    const result = await deliverablesStore.getOriginalProjectBrief(props.deliverable.id);

    if (result.success) {
      originalBriefData.value = result.brief;
      originalProjectName.value = result.projectName;
      originalProjectId.value = result.projectId;
      originalDeliverableInfo.value = {
        id: result.originalDeliverableId,
        title: result.originalDeliverableTitle
      };
    } else {
      if (result.originalDeleted) {
        originalDeliverableDeleted.value = true;
        originalBriefError.value = 'The original deliverable has been deleted';
      } else if (result.projectDeleted) {
        originalBriefError.value = 'The original project has been deleted';
      } else {
        originalBriefError.value = result.error || 'Unable to load project brief';
      }
    }
  } catch (error) {
    logger.error('[DeliverableModal] Failed to load original project brief', { error: error.message });
    originalBriefError.value = 'Failed to load project brief';
  } finally {
    loadingOriginalBrief.value = false;
  }
}

// Reset clone-related state
function resetCloneState() {
  originalBriefData.value = null;
  originalProjectName.value = null;
  originalProjectId.value = null;
  originalDeliverableInfo.value = null;
  originalDeliverableDeleted.value = false;
  originalBriefError.value = null;
}

function removeEvent(eventId) {
  // Ensure productionEventIds is an array before filtering
  const currentIds = form.value.productionEventIds || [];
  form.value.productionEventIds = currentIds.filter(id => id !== eventId);
}

function handleUserAssigned(user) {
  logger.info('[DeliverableModal] User assigned to deliverable', {
    userId: user?.id,
    userName: user?.name
  });
}

async function handleInlineUserAssigned(user) {
  logger.info('[DeliverableModal] User assigned inline', {
    userId: user?.id,
    userName: user?.name
  });

  // Only auto-save if we have an existing deliverable
  if (isExisting.value) {
    try {
      isSaving.value = true;

      // Store the old value before updating
      const oldUserId = originalAssignedUserId.value || null;

      const updateData = {
        assignedUserId: form.value.assignedUserId || null
      };
      await projectsStore.updateDeliverable(props.deliverable.id, updateData);

      logger.info('[DeliverableModal] Assigned user updated', {
        deliverableId: props.deliverable.id,
        assignedUserId: form.value.assignedUserId,
        oldUserId: oldUserId
      });

      originalAssignedUserId.value = form.value.assignedUserId;
      // Don't emit 'saved' event as it might trigger modal close
      // emit('saved', { id: props.deliverable.id, ...form.value });
    } catch (error) {
      logger.error('[DeliverableModal] Failed to update assigned user', {
        error: error.message
      });
      form.value.assignedUserId = originalAssignedUserId.value;
    } finally {
      isSaving.value = false;
    }
  }
}

async function handleInlinePrimaryEditorAssigned(user) {
  logger.info('[DeliverableModal] Primary editor assigned inline', {
    userId: user?.id,
    userName: user?.name
  });

  // Only auto-save if we have an existing deliverable
  if (isExisting.value) {
    try {
      isSaving.value = true;

      // Store the old value before updating
      const oldUserId = originalPrimaryEditorId.value || null;

      const updateData = {
        primaryEditorId: form.value.primaryEditorId || null
      };
      await projectsStore.updateDeliverable(props.deliverable.id, updateData);

      logger.info('[DeliverableModal] Primary editor updated', {
        deliverableId: props.deliverable.id,
        primaryEditorId: form.value.primaryEditorId,
        oldUserId: oldUserId
      });

      originalPrimaryEditorId.value = form.value.primaryEditorId;
    } catch (error) {
      logger.error('[DeliverableModal] Failed to update primary editor', {
        error: error.message
      });
      form.value.primaryEditorId = originalPrimaryEditorId.value;
    } finally {
      isSaving.value = false;
    }
  }
}

async function saveInlineDueDate(event) {
  // Prevent event propagation to avoid triggering modal close
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  try {
    isSaving.value = true;
    const updateData = {
      dueDate: safeTimestampFromDate(tempDueDate.value)
    };
    await projectsStore.updateDeliverable(props.deliverable.id, updateData);

    logger.info('[DeliverableModal] Due date updated', {
      deliverableId: props.deliverable.id,
      dueDate: tempDueDate.value
    });

    form.value.dueDate = tempDueDate.value;
    isEditingDueDate.value = false;
    // Don't emit 'saved' event as it might trigger modal close
    // emit('saved', { id: props.deliverable.id, ...form.value });
  } catch (error) {
    logger.error('[DeliverableModal] Failed to update due date', {
      error: error.message
    });
    tempDueDate.value = form.value.dueDate;
  } finally {
    isSaving.value = false;
  }
}

function cancelInlineDueDate() {
  tempDueDate.value = form.value.dueDate;
  isEditingDueDate.value = false;
}

async function saveInlineStage(event) {
  // Prevent event propagation to avoid triggering modal close
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  try {
    isSaving.value = true;

    // Check dependencies before allowing stage transition
    const { blocked, blockers } = await dependencyService.checkDependencies(props.deliverable, tempStage.value);
    if (blocked) {
      dependencyBlockers.value = blockers;
      dependencyTargetStage.value = tempStage.value;
      showDependencyOverride.value = true;
      isSaving.value = false;
      return;
    }

    const updateData = {
      stage: tempStage.value
    };
    await projectsStore.updateDeliverable(props.deliverable.id, updateData);

    logger.info('[DeliverableModal] Stage updated', {
      deliverableId: props.deliverable.id,
      stage: tempStage.value
    });

    form.value.stage = tempStage.value;
    isEditingStage.value = false;
  } catch (error) {
    logger[error.isValidation ? 'warn' : 'error']('[DeliverableModal] Failed to update stage', {
      error: error.message
    });
    tempStage.value = form.value.stage;
  } finally {
    isSaving.value = false;
  }
}

function cancelInlineStage() {
  tempStage.value = form.value.stage;
  isEditingStage.value = false;
}

async function navigateToLinkedDeliverable(targetId) {
  try {
    // Minimize current deliverable
    deliverablesStore.minimizeDeliverable({
      ...props.deliverable,
      project: props.project,
      company: props.company,
    });

    // Fetch and open the target deliverable
    const targetDeliverable = await deliverableService.getDeliverable(targetId);
    const targetProject = targetDeliverable.projectId
      ? projectsStore.getProjectById?.(targetDeliverable.projectId) || { id: targetDeliverable.projectId }
      : props.project;
    const targetCompany = props.company;

    deliverablesStore.openDeliverableModal(targetDeliverable, targetProject, targetCompany);
  } catch (error) {
    logger.error('[DeliverableModal] Failed to navigate to linked deliverable', { targetId, error: error.message });
    toast.error('Failed to open deliverable');
  }
}



async function saveDependencies(deps) {
  try {
    await projectsStore.updateDeliverable(props.deliverable.id, { dependencies: deps });
    form.value.dependencies = deps;
    logger.info('[DeliverableModal] Dependencies updated', { deliverableId: props.deliverable.id, count: deps.length });
  } catch (error) {
    logger.error('[DeliverableModal] Failed to update dependencies', { error: error.message });
  }
}

async function saveLinkedDeliverableIds(ids) {
  try {
    const currentId = props.deliverable.id;
    const oldIds = new Set(form.value.linkedDeliverableIds || []);
    const newIds = new Set(ids);

    // Save this deliverable's linked list
    await projectsStore.updateDeliverable(currentId, { linkedDeliverableIds: ids });
    form.value.linkedDeliverableIds = ids;

    // Add reciprocal link on newly added targets
    const added = ids.filter(id => !oldIds.has(id));
    for (const targetId of added) {
      try {
        const targetDoc = await deliverableService.getDeliverable(targetId);
        const targetLinks = new Set(targetDoc.linkedDeliverableIds || []);
        if (!targetLinks.has(currentId)) {
          targetLinks.add(currentId);
          await projectsStore.updateDeliverable(targetId, { linkedDeliverableIds: [...targetLinks] }, false);
        }
      } catch (reciprocalErr) {
        logger.warn('[DeliverableModal] Failed to add reciprocal link', { targetId, error: reciprocalErr.message });
      }
    }

    // Remove reciprocal link from removed targets
    const removed = [...oldIds].filter(id => !newIds.has(id));
    for (const targetId of removed) {
      try {
        const targetDoc = await deliverableService.getDeliverable(targetId);
        const targetLinks = (targetDoc.linkedDeliverableIds || []).filter(id => id !== currentId);
        await projectsStore.updateDeliverable(targetId, { linkedDeliverableIds: targetLinks }, false);
      } catch (unlinkErr) {
        logger.warn('[DeliverableModal] Failed to remove reciprocal link', { targetId, error: unlinkErr.message });
      }
    }

    logger.info('[DeliverableModal] Linked deliverables updated (bidirectional)', { deliverableId: currentId, count: ids.length });
  } catch (error) {
    logger.error('[DeliverableModal] Failed to update linked deliverables', { error: error.message });
  }
}

function closeDependencyOverride() {
  showDependencyOverride.value = false;
  dependencyBlockers.value = [];
  dependencyTargetStage.value = '';
  tempStage.value = form.value.stage;
  isEditingStage.value = false;
}

async function handleDependencyOverride() {
  try {
    isSaving.value = true;
    await projectsStore.updateDeliverable(props.deliverable.id, { stage: dependencyTargetStage.value });
    form.value.stage = dependencyTargetStage.value;
    tempStage.value = dependencyTargetStage.value;
    isEditingStage.value = false;
    logger.info('[DeliverableModal] Dependency override applied', {
      deliverableId: props.deliverable.id,
      targetStage: dependencyTargetStage.value,
      skippedDependencies: dependencyBlockers.value.map(b => b.id)
    });
  } catch (error) {
    logger.error('[DeliverableModal] Failed to override dependency', { error: error.message });
    tempStage.value = form.value.stage;
  } finally {
    isSaving.value = false;
    showDependencyOverride.value = false;
    dependencyBlockers.value = [];
    dependencyTargetStage.value = '';
  }
}

async function saveInlineStatus(event) {
  // Prevent event propagation to avoid triggering modal close
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  try {
    isSaving.value = true;
    const updateData = {
      status: tempStatus.value
    };
    await projectsStore.updateDeliverable(props.deliverable.id, updateData);

    logger.info('[DeliverableModal] Status updated', {
      deliverableId: props.deliverable.id,
      status: tempStatus.value
    });

    form.value.status = tempStatus.value;
    isEditingStatus.value = false;
  } catch (error) {
    logger.error('[DeliverableModal] Failed to update status', {
      error: error.message
    });
    tempStatus.value = form.value.status;
  } finally {
    isSaving.value = false;
  }
}

function cancelInlineStatus() {
  tempStatus.value = form.value.status;
  isEditingStatus.value = false;
}

function handleCancel() {
  if (isExisting.value && isEditMode.value) {
    // Cancel edit mode, go back to view mode
    isEditMode.value = false;
    // Reset form to original data
    if (props.deliverable) {
      form.value = {
        title: props.deliverable.title || '',
        type: props.deliverable.type || 'standard',
        status: props.deliverable.status || DELIVERABLE_STATUSES.NOT_STARTED,
        stage: props.deliverable.stage || DELIVERABLE_STAGES.NOT_READY,
        aspectRatio: props.deliverable.aspectRatio || '16:9',
        runtime: props.deliverable.runtime || '',
        resolution: props.deliverable.resolution || '1080p',
        weight: props.deliverable.weight ?? null,
        description: props.deliverable.description || '',
        dueDate: props.deliverable.dueDate?.toDate ? props.deliverable.dueDate.toDate() : props.deliverable.dueDate,
        productionEventIds: props.deliverable.productionEventIds || [],
        assignedUserId: props.deliverable.assignedUserId || ''
      };
    }
  } else {
    isOpen.value = false;
    resetForm();
  }
}

function formatEventDate(date) {
  if (!date) return '';
  
  const eventDate = date.toDate ? date.toDate() : new Date(date);
  return eventDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: eventDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

function formatEventDateTime(date) {
  if (!date) return '';
  
  const eventDate = date.toDate ? date.toDate() : new Date(date);
  const dateStr = eventDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: eventDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
  const timeStr = eventDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return `${dateStr} at ${timeStr}`;
}

function getAspectRatioLabel(ratio) {
  const ratioLabels = {
    '16:9': '16:9 (Landscape)',
    '9:16': '9:16 (Portrait)',
    '1:1': '1:1 (Square)',
    '4:5': '4:5 (Vertical)',
    'custom': 'Custom'
  };
  return ratioLabels[ratio] || ratio;
}

// Helper methods for type and status display
function getTypeIcon(type) {
  return getDeliverableTypeIcon(type);
}

function getTypeColor(type) {
  return getDeliverableTypeColor(type);
}

function getTypeLabel(type) {
  return getDeliverableTypeLabel(type);
}

function getStatusIcon(status) {
  return getDeliverableStatusIcon(status);
}

function getStatusClass(status) {
  return getDeliverableStatusClassName(status);
}

function getStatusLabel(status) {
  return getDeliverableStatusLabel(status);
}

// Helper methods for stage display
function getStageIcon(stage) {
  return getDeliverableStageIcon(stage);
}

function getStageClass(stage) {
  return getDeliverableStageClassName(stage);
}

function getStageLabel(stage) {
  return getDeliverableStageLabel(stage);
}

// Preload all user data when messages load
async function preloadUsers(messagesToPreload) {
  if (!messagesToPreload || messagesToPreload.length === 0) return;

  // Get unique user IDs from messages and reactions
  const userIds = new Set();

  messagesToPreload.forEach(msg => {
    // Add message author
    if (msg.userId) userIds.add(msg.userId);

    // Add users from reactions
    if (msg.reactions) {
      Object.values(msg.reactions).forEach(reaction => {
        if (reaction.userId) userIds.add(reaction.userId);
      });
    }

    // Add users from readBy
    if (msg.readBy) {
      Object.keys(msg.readBy).forEach(userId => {
        userIds.add(userId);
      });
    }
  });

  // Filter out already cached users
  const usersToFetch = Array.from(userIds).filter(userId =>
    !userDataCache.value.has(userId)
  );

  // Fetch all uncached users in parallel
  if (usersToFetch.length > 0) {
    const fetchPromises = usersToFetch.map(userId =>
      fetchUserFromFirestore(userId)
    );

    await Promise.all(fetchPromises);
  }
}

// Discussion methods
function scrollToBottomOfChat() {
  nextTick(() => {
    if (chatMessages.value) {
      chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
    }
  });
}

async function loadMessages() {
  if (!props.deliverable?.id) return;

  // Load mentionable users for highlight validation (if not already loaded)
  if (allMentionableUsers.value.length === 0) {
    try {
      allMentionableUsers.value = await userService.getAdminUsers();
    } catch { /* ignore - mentions just won't be highlighted */ }
  }

  try {
    loadingMessages.value = true;

    // Subscribe to real-time updates
    if (discussionUnsubscribe.value) {
      discussionUnsubscribe.value();
    }

    discussionUnsubscribe.value = discussionService.subscribeToMessages(
      props.deliverable.id,
      async (updatedMessages) => {
        // Check if this is a new message (not just a read receipt update)
        const isNewMessage = updatedMessages.length > messages.value.length;

        // Save current scroll position before updating
        const scrollContainer = chatMessages.value;
        const scrollPosition = scrollContainer ? scrollContainer.scrollTop : 0;
        const scrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
        const wasAtBottom = scrollContainer ?
          (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 10) : false;

        // Preload users for all messages
        await preloadUsers(updatedMessages);

        messages.value = updatedMessages;

        // Only auto-scroll if it's a new message and we were at the bottom
        if (isNewMessage && wasAtBottom) {
          scrollToBottomOfChat();
        } else if (!isNewMessage && scrollContainer) {
          // Restore scroll position after read receipt update
          nextTick(() => {
            if (scrollContainer && scrollContainer.scrollHeight > 0) {
              // Calculate the difference in scroll height
              const heightDiff = scrollContainer.scrollHeight - scrollHeight;
              // Maintain relative scroll position
              scrollContainer.scrollTop = scrollPosition + heightDiff;
            }
          });
        }
      }
    );

    // Initial load
    const initialMessages = await discussionService.getMessages(props.deliverable.id);

    // Preload all users before setting messages
    await preloadUsers(initialMessages);

    messages.value = initialMessages;

    // Scroll to bottom after initial load
    scrollToBottomOfChat();
  } catch (error) {
    logger.error('[DeliverableModal] Failed to load messages', {
      error: error.message
    });
  } finally {
    loadingMessages.value = false;
  }
}

async function sendMessage() {
  const editor = messageEditor.value;
  if (!editor) return;
  const hasText = editor.textContent.trim();
  const hasAttachment = pendingAttachment.value?.url;
  if ((!hasText && !hasAttachment) || !props.deliverable?.id) return;

  try {
    sendingMessage.value = true;

    // Extract mention user IDs from DOM spans
    const mentionIds = [...editor.querySelectorAll('.mention[data-user-id]')]
      .map(el => el.dataset.userId);

    // Get sanitized HTML
    const html = hasText ? sanitizeHTML(editor.innerHTML) : '';

    // Build attachments array
    const attachments = [];
    if (hasAttachment) {
      const att = pendingAttachment.value;
      attachments.push({
        type: att.type,
        url: att.url,
        storagePath: att.storagePath,
        fileName: att.fileName,
        contentType: att.contentType,
        size: att.size
      });
    }

    await discussionService.addMessage(
      props.deliverable.id,
      html,
      mentionIds,
      attachments
    );

    editor.innerHTML = '';
    editorIsEmpty.value = true;
    removeAttachment();
  } catch (error) {
    logger.error('[DeliverableModal] Failed to send message', {
      error: error.message
    });
  } finally {
    sendingMessage.value = false;
  }
}

function handleMessageKeydown(event) {
  // Handle formatting shortcuts (Ctrl/Cmd + B/I/U)
  const mod = event.metaKey || event.ctrlKey;
  if (mod && !event.shiftKey) {
    if (event.key === 'b' || event.key === 'B') {
      event.preventDefault();
      applyFormat('bold');
      return;
    }
    if (event.key === 'i' || event.key === 'I') {
      event.preventDefault();
      applyFormat('italic');
      return;
    }
    if (event.key === 'u' || event.key === 'U') {
      event.preventDefault();
      applyFormat('underline');
      return;
    }
  }

  // Handle mention suggestions navigation
  if (showMentionSuggestions.value) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectedMentionIndex.value = Math.min(selectedMentionIndex.value + 1, mentionSuggestions.value.length - 1);
      return;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectedMentionIndex.value = Math.max(selectedMentionIndex.value - 1, 0);
      return;
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      if (mentionSuggestions.value.length > 0) {
        event.preventDefault();
        insertMention(mentionSuggestions.value[selectedMentionIndex.value]);
        return;
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      showMentionSuggestions.value = false;
      return;
    }
  }

  // Enter → send or save edit
  if (event.key === 'Enter' && !event.shiftKey && !showMentionSuggestions.value) {
    event.preventDefault();
    if (editingMessageId.value) {
      saveEditMessage();
    } else {
      sendMessage();
    }
  } else if (event.key === 'Escape' && editingMessageId.value) {
    event.preventDefault();
    cancelEditMessage();
  } else if (event.key === 'Enter' && event.shiftKey) {
    // Prevent default div insertion, insert <br> instead
    event.preventDefault();
    document.execCommand('insertLineBreak');
  }
}

function handleEditorFocus() {
  if (editorBlurTimeout) {
    clearTimeout(editorBlurTimeout);
    editorBlurTimeout = null;
  }
  editorFocused.value = true;
  document.addEventListener('selectionchange', updateActiveFormats);
}

function handleEditorBlur() {
  // Small delay so toolbar button clicks register before hiding
  editorBlurTimeout = setTimeout(() => {
    editorFocused.value = false;
    document.removeEventListener('selectionchange', updateActiveFormats);
  }, 150);
}

function updateActiveFormats() {
  activeFormats.value = {
    bold: document.queryCommandState('bold'),
    italic: document.queryCommandState('italic'),
    underline: document.queryCommandState('underline'),
  };
}

function handleEditorInput() {
  // Update empty state
  const editor = messageEditor.value;
  editorIsEmpty.value = !editor || !editor.textContent.trim();

  // Detect @ mentions using cursor position in the DOM
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    showMentionSuggestions.value = false;
    return;
  }

  const node = sel.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE) {
    showMentionSuggestions.value = false;
    return;
  }

  // Walk backwards from cursor position in this text node to find @
  const text = node.textContent.substring(0, sel.anchorOffset);
  const lastAt = text.lastIndexOf('@');

  if (lastAt !== -1) {
    const afterAt = text.substring(lastAt + 1);
    // Only trigger if no spaces between @ and cursor
    if (!afterAt.includes(' ')) {
      mentionSearchTerm.value = afterAt.toLowerCase();
      updateMentionSuggestions();
      showMentionSuggestions.value = true;
      selectedMentionIndex.value = 0;
      return;
    }
  }

  showMentionSuggestions.value = false;
}

function handleEditorPaste(event) {
  // Check for pasted images first
  if (!pendingAttachment.value && event.clipboardData?.items) {
    for (const item of event.clipboardData.items) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) handleImageSelect(file);
        return;
      }
    }
  }

  // Fallback: paste as plain text
  event.preventDefault();
  const text = event.clipboardData.getData('text/plain');
  document.execCommand('insertText', false, text);
}

async function updateMentionSuggestions() {
  try {
    // Get admin users for mentions
    const users = await userService.getAdminUsers();
    const currentUserId = userAuthStore.user.id;

    // Cache all users for mention resolution when sending messages
    allMentionableUsers.value = users;

    // Filter based on search term and exclude current user
    mentionSuggestions.value = users.filter(user => {
      // Exclude current user from suggestions
      if (user.id === currentUserId) return false;

      const name = (user.name || user.email || '').toLowerCase();
      return name.includes(mentionSearchTerm.value);
    }).slice(0, 5); // Limit to 5 suggestions
  } catch (error) {
    logger.error('[DeliverableModal] Failed to load mention suggestions', {
      error: error.message
    });
    mentionSuggestions.value = [];
  }
}

function insertMention(user) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const node = sel.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE) return;

  const text = node.textContent;
  const cursorOffset = sel.anchorOffset;
  const textBefore = text.substring(0, cursorOffset);
  const lastAt = textBefore.lastIndexOf('@');

  if (lastAt === -1) return;

  const userName = user.name || user.email.split('@')[0];

  // Create the mention span
  const mentionSpan = document.createElement('span');
  mentionSpan.className = 'mention';
  mentionSpan.setAttribute('contenteditable', 'false');
  mentionSpan.setAttribute('data-user-id', user.id);
  mentionSpan.textContent = `@${userName}`;

  // Create trailing space text node
  const spaceNode = document.createTextNode('\u00A0');

  // Split the text node: before @ | mention | after cursor
  const afterText = text.substring(cursorOffset);
  const beforeText = text.substring(0, lastAt);

  // Replace the text node content
  node.textContent = beforeText;

  // Insert mention span and space after the text node
  const parent = node.parentNode;
  if (!parent) return; // Guard against detached DOM nodes during unmount/navigation
  const nextSibling = node.nextSibling;

  if (nextSibling) {
    parent.insertBefore(mentionSpan, nextSibling);
    parent.insertBefore(spaceNode, nextSibling);
    if (afterText) {
      parent.insertBefore(document.createTextNode(afterText), nextSibling);
    }
  } else {
    parent.appendChild(mentionSpan);
    parent.appendChild(spaceNode);
    if (afterText) {
      parent.appendChild(document.createTextNode(afterText));
    }
  }

  // Place cursor after the space
  const range = document.createRange();
  range.setStartAfter(spaceNode);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);

  showMentionSuggestions.value = false;
  editorIsEmpty.value = false;
}

function applyFormat(type) {
  const commands = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline'
  };
  document.execCommand(commands[type], false, null);
  updateActiveFormats();
}

// Image attachment functions
async function handleImageSelect(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    toast.error('Only PNG, JPEG, GIF, and WebP images are allowed');
    return;
  }
  if (file.size > MAX_IMAGE_SIZE) {
    toast.error('Image must be smaller than 10 MB');
    return;
  }

  // Create blob preview immediately
  const objectUrl = URL.createObjectURL(file);
  pendingAttachment.value = {
    type: 'image',
    fileName: file.name,
    contentType: file.type,
    size: file.size,
    objectUrl,
    url: null,
    storagePath: null
  };

  // Upload in background
  uploadingImage.value = true;
  try {
    const result = await discussionService.uploadDiscussionImage(props.deliverable.id, file);
    if (pendingAttachment.value) {
      pendingAttachment.value.url = result.url;
      pendingAttachment.value.storagePath = result.storagePath;
    }
  } catch {
    toast.error('Failed to upload image');
    removeAttachment();
  } finally {
    uploadingImage.value = false;
  }
}

function removeAttachment() {
  if (pendingAttachment.value?.objectUrl) {
    URL.revokeObjectURL(pendingAttachment.value.objectUrl);
  }
  pendingAttachment.value = null;
}

function triggerImagePicker() {
  imageInputRef.value?.click();
}

function onFileInputChange(event) {
  const file = event.target.files?.[0];
  if (file) {
    handleImageSelect(file);
  }
  // Reset so the same file can be re-selected
  event.target.value = '';
}

async function startEditMessage(message) {
  if (message.userId !== userAuthStore.user.id) return;

  editingMessageId.value = message.id;
  editingMessageText.value = message.message;

  // Populate attachment if message has one
  if (message.attachments?.length) {
    const att = message.attachments[0];
    pendingAttachment.value = {
      type: att.type,
      url: attSrc(att),
      storagePath: att.storagePath,
      fileName: att.fileName,
      contentType: att.contentType,
      size: att.size,
      objectUrl: null // Use url for preview since original blob is gone
    };
  }

  // Load message content into the main composer
  nextTick(() => {
    const editor = messageEditor.value;
    if (editor && typeof editor.focus === 'function') {
      if (isHTMLMessage(message.message)) {
        editor.innerHTML = sanitizeHTML(message.message);
      } else {
        editor.textContent = message.message;
      }
      editorIsEmpty.value = false;
      editor.focus();
    }
  });
}

async function saveEditMessage() {
  const editor = messageEditor.value;
  if (!editor) return;
  const hasText = editor.textContent.trim();
  const hasAttachment = pendingAttachment.value?.url;
  if (!hasText && !hasAttachment) return;
  if (!editingMessageId.value) return;

  try {
    // Extract mention user IDs from DOM spans
    const mentionIds = [...editor.querySelectorAll('.mention[data-user-id]')]
      .map(el => el.dataset.userId);

    const html = hasText ? sanitizeHTML(editor.innerHTML) : '';

    // Build attachments for edit
    const attachments = [];
    if (hasAttachment) {
      const att = pendingAttachment.value;
      attachments.push({
        type: att.type,
        url: att.url,
        storagePath: att.storagePath,
        fileName: att.fileName,
        contentType: att.contentType,
        size: att.size
      });
    }

    await discussionService.editMessage(
      props.deliverable.id,
      editingMessageId.value,
      html,
      mentionIds,
      attachments
    );

    editingMessageId.value = null;
    editingMessageText.value = '';
    editor.innerHTML = '';
    editorIsEmpty.value = true;
    removeAttachment();
  } catch (error) {
    logger.error('[DeliverableModal] Failed to edit message', {
      error: error.message
    });
  }
}

function cancelEditMessage() {
  editingMessageId.value = null;
  editingMessageText.value = '';
  const editor = messageEditor.value;
  if (editor) {
    editor.innerHTML = '';
  }
  editorIsEmpty.value = true;
  removeAttachment();
}

async function deleteMessage(message) {
  if (message.userId !== userAuthStore.user.id) return;

  // Play the "Really?" audio when delete is clicked
  const audio = new Audio('/assets/Really.wav');
  audio.volume = 0.5;
  audio.play().catch(() => { /* Audio playback failed - ignore */ });

  // Show confirmation in the hover bubble
  deleteConfirmMessageId.value = message.id;
}

async function confirmDeleteMessage(message) {
  try {
    await discussionService.deleteMessage(
      props.deliverable.id,
      message.id
    );
    deleteConfirmMessageId.value = null;
  } catch (error) {
    logger.error('[DeliverableModal] Failed to delete message', {
      error: error.message
    });
  }
}

function cancelDeleteMessage() {
  deleteConfirmMessageId.value = null;
}

async function toggleReaction(message, emoji) {
  try {
    await discussionService.toggleReaction(props.deliverable.id, message.id, emoji);
    showEmojiPicker.value = null;
  } catch (error) {
    logger.error('[DeliverableModal] Failed to toggle reaction', {
      error: error.message
    });
  }
}

function getGroupedReactions(message) {
  if (!message.reactions || Object.keys(message.reactions).length === 0) {
    return [];
  }

  // Group reactions by emoji
  const grouped = {};
  const currentUserId = userAuthStore.user.id;

  Object.entries(message.reactions).forEach(([, reactionData]) => {
    const emoji = reactionData.emoji;
    const userId = reactionData.userId;

    if (!grouped[emoji]) {
      grouped[emoji] = {
        emoji,
        count: 0,
        users: [],
        userIds: [],
        hasUserReacted: false
      };
    }

    grouped[emoji].count++;
    grouped[emoji].users.push(reactionData.userName || 'Unknown');
    grouped[emoji].userIds.push(userId);

    if (userId === currentUserId) {
      grouped[emoji].hasUserReacted = true;
    }
  });

  return Object.values(grouped);
}

function getMessageReadReceipts(message) {
  // Only show read receipts for the message sender
  if (message.userId !== userAuthStore.user.id) {
    return [];
  }

  if (!message.readBy || Object.keys(message.readBy).length === 0) {
    return [];
  }

  // Get all users who have read the message (excluding the sender)
  const readers = [];
  Object.entries(message.readBy).forEach(([userId, data]) => {
    if (userId !== message.userId) {
      readers.push({
        userId,
        userName: data.userName,
        userAvatar: data.userAvatar,
        timestamp: data.timestamp
      });
    }
  });

  return readers;
}


// Get display text for date dividers
function getDateDisplay(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    // For older dates, show the full date
    return messageDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}

// Format message time to show only time (not date)
function formatMessageTime(timestamp) {
  if (!timestamp) return '';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

  // Always show time in 12-hour format
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function getUserInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

function getUserAvatar(userId) {
  if (!userId) return null;

  // Check cache first (synchronous)
  const cachedUser = userDataCache.value.get(userId);
  if (cachedUser) {
    return cachedUser.avatar || null;
  }

  // Fallback to current user if it's them (shouldn't normally hit this if cache is populated)
  if (userId === userAuthStore.user?.id) {
    const currentUser = userAuthStore.user;
    return currentUser?.profile?.avatar || currentUser?.avatar || currentUser?.photoURL || null;
  }

  return null;
}

function getUserFullName(userId, fallbackName = '') {
  if (!userId) return fallbackName || 'Unknown User';

  // Check cache first (synchronous)
  const cachedUser = userDataCache.value.get(userId);
  if (cachedUser) {
    // Priority: full name > firstName+lastName > displayName > email prefix
    if (cachedUser.name && !cachedUser.name.includes('@')) {
      return cachedUser.name;
    }
    if (cachedUser.firstName && cachedUser.lastName) {
      return `${cachedUser.firstName} ${cachedUser.lastName}`;
    }
    if (cachedUser.displayName && !cachedUser.displayName.includes('@')) {
      return cachedUser.displayName;
    }
    if (cachedUser.firstName) {
      return cachedUser.firstName;
    }
  }

  // Fallback to current user if it's them
  if (userId === userAuthStore.user?.id) {
    const currentUser = userAuthStore.user;
    if (currentUser.name && !currentUser.name.includes('@')) {
      return currentUser.name;
    }
  }

  // Fallback logic
  if (fallbackName && !fallbackName.includes('@')) {
    return fallbackName;
  }
  if (fallbackName && fallbackName.includes('@')) {
    return fallbackName.split('@')[0];
  }

  return fallbackName || 'Unknown User';
}

/**
 * Detect if a message is HTML (new format) or plain text (old format)
 */
function isHTMLMessage(text) {
  return /<(strong|em|u|br|ul|ol|li|input|span\s+class="mention")/.test(text);
}

/**
 * Sanitize HTML from the contenteditable editor.
 * - Normalize <b> → <strong>, <i> → <em>
 * - Convert Chrome's <div><br></div> line breaks → <br>
 * - Strip all tags except allowed ones
 */
function sanitizeHTML(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  // Normalize block-level divs to <br> (Chrome wraps lines in <div>)
  body.querySelectorAll('div').forEach(div => {
    // Replace <div><br></div> or <div>text</div> with <br>text
    const br = doc.createElement('br');
    div.before(br);
    while (div.firstChild) {
      div.before(div.firstChild);
    }
    div.remove();
  });

  // Normalize <b> → <strong>, <i> → <em>
  body.querySelectorAll('b').forEach(b => {
    const strong = doc.createElement('strong');
    while (b.firstChild) strong.appendChild(b.firstChild);
    b.replaceWith(strong);
  });
  body.querySelectorAll('i').forEach(i => {
    const em = doc.createElement('em');
    while (i.firstChild) em.appendChild(i.firstChild);
    i.replaceWith(em);
  });

  // Recursively build clean HTML allowing only safe tags
  const allowedTags = new Set(['STRONG', 'EM', 'U', 'BR', 'SPAN']);

  function cleanNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const tag = node.tagName;

    // Special handling for mention spans
    if (tag === 'SPAN' && node.classList.contains('mention') && node.dataset.userId) {
      const userId = node.dataset.userId;
      const text = node.textContent;
      return `<span class="mention" contenteditable="false" data-user-id="${userId}">${escapeHTML(text)}</span>`;
    }

    // Allowed tags: recurse into children
    if (allowedTags.has(tag) && tag !== 'SPAN') {
      if (tag === 'BR') return '<br>';
      const children = [...node.childNodes].map(cleanNode).join('');
      return `<${tag.toLowerCase()}>${children}</${tag.toLowerCase()}>`;
    }

    // Unwrap disallowed tags, keep children
    return [...node.childNodes].map(cleanNode).join('');
  }

  let result = [...body.childNodes].map(cleanNode).join('');

  // Clean up leading/trailing <br>
  result = result.replace(/^(<br>)+/, '').replace(/(<br>)+$/, '');

  return result;
}

function escapeHTML(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Highlight mentions and format messages for display.
 * Handles both old plain-text messages and new HTML messages.
 */
function highlightMentions(text, validUsers = []) {
  // New HTML messages: already contain <strong>, <em>, <u>, <span class="mention">
  if (isHTMLMessage(text)) {
    // Sanitize for safety then auto-link URLs in text nodes
    let result = sanitizeHTML(text);
    // Auto-link URLs
    result = result.replace(/(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      const displayUrl = url.length > 50 ? url.substring(0, 47) + '...' : url;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="message-link">${displayUrl}</a>`;
    });
    return result;
  }

  // Old plain-text messages: apply markdown formatting and mention parsing
  let result = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Extract URLs into placeholders so formatting markers inside URLs aren't corrupted
  const urlPattern = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  const urls = [];
  result = result.replace(urlPattern, (url) => {
    const index = urls.length;
    urls.push(url);
    return `\u200BURL${index}\u200B`;
  });

  // Parse formatting: bold (**text**) first, then italic (*text*), then underline (__text__)
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  result = result.replace(/__(.+?)__/g, '<u>$1</u>');

  // Restore URL placeholders as clickable links
  result = result.replace(/\u200BURL(\d+)\u200B/g, (_match, index) => {
    const url = urls[parseInt(index)];
    const href = url.startsWith('www.') ? `https://${url}` : url;
    const displayUrl = url.length > 50 ? url.substring(0, 47) + '...' : url;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="message-link">${displayUrl}</a>`;
  });

  // Then handle @mentions - only highlight valid users
  result = result.replace(/@([\w_]+)/g, (_match, name) => {
    // Convert underscores back to spaces for display
    const displayName = name.replace(/_/g, ' ');

    // Check if this is a valid user
    const isValidUser = validUsers.some(user => {
      const userName = (user.name || '').toLowerCase();
      const userEmail = (user.email || '').split('@')[0].toLowerCase();
      const mentionName = displayName.toLowerCase();
      return userName === mentionName || userEmail === mentionName;
    });

    if (isValidUser) {
      return `<span class="mention">@${displayName}</span>`;
    }
    return `@${displayName}`;
  });

  return result;
}

// Intersection Observer for tracking viewed messages
const messageObserver = ref(null);
const observedMessages = ref(new Set());

// Batch messages to mark as read with debounce
let pendingReadMessages = [];
let markAsReadTimeout = null;

function batchMarkAsRead() {
  if (markAsReadTimeout) {
    clearTimeout(markAsReadTimeout);
  }

  markAsReadTimeout = setTimeout(() => {
    if (pendingReadMessages.length > 0 && props.deliverable?.id) {
      const messagesToMark = [...pendingReadMessages];
      pendingReadMessages = [];
      discussionService.markMessagesAsRead(props.deliverable.id, messagesToMark);
    }
  }, 500); // Wait 500ms to batch multiple messages
}

// Set up Intersection Observer to track when messages are viewed
function setupMessageObserver() {
  if (!props.deliverable?.id) return;

  messageObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const messageId = entry.target.dataset.messageId;
          if (messageId && !observedMessages.value.has(messageId)) {
            pendingReadMessages.push(messageId);
            observedMessages.value.add(messageId);
          }
        }
      });

      // Batch mark messages as read with debounce
      if (pendingReadMessages.length > 0) {
        batchMarkAsRead();
      }
    },
    {
      root: chatMessages.value,
      rootMargin: '0px',
      threshold: 0.5 // Message is considered viewed when 50% visible
    }
  );

  // Start observing messages after a short delay
  nextTick(() => {
    observeMessages();
  });
}

// Observe message elements
function observeMessages() {
  if (!messageObserver.value) return;

  // Find all message elements and observe them
  const messageElements = document.querySelectorAll('.message-row[data-message-id]');
  messageElements.forEach((element) => {
    messageObserver.value.observe(element);
  });
}

// Remove deliverable ID from URL
function removeDeliverableFromUrl() {
  try {
    const currentQuery = { ...router.currentRoute.value.query };
    if (currentQuery.deliverable) {
      delete currentQuery.deliverable;
      router.replace({
        path: router.currentRoute.value.path,
        query: currentQuery
      });
    }
  } catch (err) {
    logger.error('[DeliverableModal] Error removing deliverable from URL', { error: err.message });
  }
}

// Load calendar events on mount
onMounted(async () => {
  try {
    logger.info('[DeliverableModal] Component mounted, checking calendar events');

    // Note: URL is already updated by the store's openDeliverableModal function
    // No need to update it again here

    // Ensure calendar store is initialized (should already be done by App.vue)
    if (!calendarStore.initialized) {
      logger.info('[DeliverableModal] Calendar store not initialized, initializing now');
      await calendarStore.init();
    }

    // Always load admin users for avatars to ensure consistency
    logger.info('[DeliverableModal] Loading admin users for avatars');
    try {
      await userAuthStore.listAllUsers();
    } catch {
      logger.warn('[DeliverableModal] Failed to load admin users for avatars (non-critical)');
    }

    // Log available events (they're already loaded and filtered by computed property)
    const eventCount = calendarStore.allEvents ? calendarStore.allEvents.length : 0;
    logger.info('[DeliverableModal] Calendar events available', {
      totalEvents: eventCount,
      companyId: props.company?.id,
      currentUser: {
        id: userAuthStore.user.id,
        name: userAuthStore.user.name,
        avatar: userAuthStore.user.avatar,
        photoURL: userAuthStore.user.photoURL
      }
    });

    // Set up message observer for read receipts
    if (props.deliverable?.id) {
      setupMessageObserver();
    }
  } catch (error) {
    logger.error('[DeliverableModal] Error initializing calendar store', {
      error: error.message,
      companyId: props.company?.id
    });
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (discussionUnsubscribe.value) {
    discussionUnsubscribe.value();
    discussionUnsubscribe.value = null;
  }

  // Clean up Intersection Observer
  if (messageObserver.value) {
    messageObserver.value.disconnect();
    messageObserver.value = null;
  }
  observedMessages.value.clear();

  // Clear pending read messages timeout
  if (markAsReadTimeout) {
    clearTimeout(markAsReadTimeout);
    markAsReadTimeout = null;
  }
  pendingReadMessages = [];

  // Clear editor blur timeout and selection listener
  document.removeEventListener('selectionchange', updateActiveFormats);
  if (editorBlurTimeout) {
    clearTimeout(editorBlurTimeout);
    editorBlurTimeout = null;
  }
});
</script>

<style scoped lang="scss">
// Modal header button styles
// The header used the dark InfoBar language, which the app has moved off.
// Same treatment as PageHeader now: no fill, a hairline rule closing it off.
:deep(.modal-card-head) {
  background: var(--bulma-scheme-main) !important;
  border-bottom: 1px solid var(--bulma-border-weak);
  // Bulma's 20px all round made a tall band for what is now a micro-label.
  padding: 0.6rem 1.25rem;
}

:deep(.modal-card-title) {
  // Same micro-label metrics as PageHeader's section marker, so the modal's
  // subject line reads in the app's own language rather than as a headline.
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bulma-grey-dark);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  // The clone tag would otherwise outweigh the title beside it.
  .tag {
    font-size: 0.65rem;
    letter-spacing: 0;
    text-transform: none;
    margin-left: 0 !important;
  }
}

.modal-header-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.modal-button-circular {
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem !important;

  .icon {
    margin: 0 !important;
    font-size: 0.75rem !important;
  }

  &.is-warning {
    &:hover:not(:disabled) {
      filter: brightness(1.1);
    }
  }

  &.is-danger {
    &:hover {
      filter: brightness(1.1);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.activity-tab {
  min-height: 400px;
}

.brief-container {
  padding: 1rem;
  height: 100%;
  overflow-y: auto;

  // Ensure brief content displays properly
  :deep(.project-brief) {
    max-width: 100%;
  }
}

.deliverable-view {
  .detail-item {
    margin-bottom: 1rem;
  }
}

.detail-item {
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 0.75rem;
  position: relative;
  overflow: visible !important;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    color: var(--bulma-text-light);
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .description-text {
    white-space: pre-wrap;
  }
}

.tags {
  flex-wrap: wrap;
  
  .tag {
    margin-bottom: 0.25rem;
  }
}

// Fix for dropdown in modal - from CLAUDE.md documented pattern
.deliverable-modal-overflow {
  :deep(.modal-card) {
    overflow: visible !important;
  }

  :deep(.modal-card-body) {
    overflow: visible !important;
    min-height: 200px;
  }

  :deep(.dropdown-menu) {
    z-index: 99999 !important;
    position: fixed !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  // Specific fix for inline editing dropdowns
  :deep(.dropdown.is-active .dropdown-menu) {
    position: fixed !important;
    z-index: 99999 !important;
  }
}

// Wide modal styling for larger modals
.wide-deliverable-modal {
  // AppModal's ROOT element is `.modal`, and this class lands on that same
  // element — so `:deep(.modal)` was a descendant selector matching nothing.
  // The gutter belongs here, on the element itself.
  //
  // 1.5rem top and bottom, and the card simply fills what is left (`flex: 1`,
  // no vh) — so there is no leftover slack to guess at, and the header always
  // clears anything pinned to the top edge.
  padding: 1.5rem 0;

  :deep(.modal-background) {
    background-color: rgba(0, 0, 0, 0.6) !important;
  }

  // Bulma reserves 32px of padding under the card and caps `.modal-card` at
  // `calc(100vh - 40px)`, so asking for 97vh was silently clamped back to 940px
  // and still overflowed the padded box. Reclaim both.
  :deep(.modal-card) {
    // Was 90vw capped at 1400px / 85vh, which left a wide display half empty.
    width: 98vw;
    max-width: none;
    height: auto;
    max-height: none;
    // basis 0, not auto: with `auto` the card sizes to its CONTENT first and
    // overflowed the viewport by 33px at each end. `min-height: 0` is what
    // actually lets a flex item shrink below its content — without it the body
    // never gets to scroll internally.
    flex: 1 1 0;
    min-height: 0;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(0, 0, 0, 0.1) !important; // Large, soft shadow for depth
    animation: slideUpModal 0.3s ease-out !important;
  }

  // When modal is closing
  &.is-closing :deep(.modal-card) {
    animation: slideDownModal 0.3s ease-in !important;
  }

  // Modal slide-up animation (entrance)
  @keyframes slideUpModal {
    from {
      transform: translateY(100px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  // Modal slide-down animation (exit)
  @keyframes slideDownModal {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100px);
      opacity: 0;
    }
  }

  :deep(.modal-card-body) {
    height: calc(85vh - 96px); // Subtract header height
    overflow: hidden;
    padding: 0; // Remove padding from modal body
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
}

// Two-column layout for deliverable details
.deliverable-details-container {
  height: 100%;
  display: flex;
  overflow: hidden;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;

  .form-column {
    background: var(--bulma-scheme-main);
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    border-bottom-left-radius: 6px;
    
    // Add visual separator with spacing
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--bulma-border);
    }
    
    // Tabs should not cause overflow
    .tabs {
      flex-shrink: 0;
    }
    
    // Tab content should fill remaining space
    .tab-content {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;

      .deliverable-view,
      .deliverable-form {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 1rem;
      }
    }

    .activity-tab-content {
      overflow-y: auto;
      overflow-x: hidden;
    }

    .review-tab-content {
      overflow: hidden !important;
      padding-right: 0 !important;
    }

    // Hide right-side separator when full-width
    &.is-12::after {
      display: none;
    }
    
    // Fix columns inside form to prevent overflow
    .columns {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
  
  .discussion-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--bulma-scheme-main-bis);
    padding-left: 2rem;
    border-bottom-right-radius: 6px;
  }
}

// Global fix for SearchableSelect dropdown in modals
:deep(.dropdown-menu) {
  max-height: 240px !important;
  overflow-y: auto !important;
}

// Fix for inline editing dropdowns - position above the field
.status-inline-select,
.stage-inline-select {
  :deep(.dropdown) {
    position: static !important;
  }

  :deep(.dropdown-menu) {
    position: absolute !important;
    bottom: 100% !important;  // Position above instead of below
    top: auto !important;
    left: 0 !important;
    right: auto !important;
    min-width: 250px !important;
    z-index: 9999 !important;
    margin-bottom: 0.5rem !important;  // Add some spacing
  }
}

// Make linked event tags look clickable
.tags {
  a.tag {
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

// Inline editing styles
.inline-editable {
  position: relative;
  overflow: visible !important;

  .clickable-field {
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;

    &:hover {
      .edit-hint {
        opacity: 1;
      }
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.6;

      &:hover {
        .edit-hint {
          opacity: 0;
        }
      }

      .tag.is-disabled {
        opacity: 0.7;
      }
    }
  }

  .edit-hint {
    color: var(--bulma-text-light);
    font-size: 0.875rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
}

// Discussion/Chat styles
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.5rem;
}


.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 0;
  min-height: 0;
}

.deleted-message-centered {
  text-align: center;
  padding: 0.75rem 0;
  animation: messageSlideIn 0.3s ease;

  .deleted-text {
    font-size: 0.75rem;
    color: hsl(0, 0%, 65%);
    font-style: italic;
  }
}

// Day divider styles (similar to deleted message style)
.day-divider {
  text-align: center;
  margin: 1.5rem 0 1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: hsl(0, 0%, 90%);
    z-index: 0;
  }

  .day-divider-text {
    font-size: 0.75rem;
    color: hsl(0, 0%, 48%);
    font-weight: 600;
    background: #f9fafb;
    padding: 0.25rem 1rem;
    border-radius: 12px;
    display: inline-block;
    position: relative;
    z-index: 1;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.message-row {
  display: flex;
  align-items: flex-start; // Align avatars with top of message container
  margin-bottom: 1rem;
  padding: 0.25rem 1rem;
  animation: messageSlideIn 0.3s ease;
  position: relative; // Positioning context for emoji picker
  &.is-editing .message-bubble {
    background-color: var(--bulma-warning) !important;
    color: var(--bulma-warning-invert, rgba(0, 0, 0, 0.7)) !important;
    transition: background-color 0.2s ease;
  }

  &.is-mine {
    justify-content: flex-end;

    .message-container {
      align-items: flex-end;
      margin-right: 0.5rem;
    }

    .message-bubble {
      background-color: hsl(217, 71%, 53%);
      color: white;
      border-bottom-right-radius: 4px;

      // Fix mention visibility in own messages (blue background)
      :deep(.mention) {
        background-color: rgba(255, 255, 255, 0.25) !important;
        color: white !important;
        border: 1px solid rgba(255, 255, 255, 0.4);
      }

      // Fix link visibility in own messages (blue background)
      :deep(.message-link) {
        color: white !important;
        text-decoration: underline;

        &:hover {
          color: rgba(255, 255, 255, 0.85) !important;
        }
      }
    }

    .message-footer {
      justify-content: flex-end;
    }

    .message-hover-actions {
      right: 0;
      left: auto;
    }
  }

  &.is-other {
    justify-content: flex-start;

    .message-container {
      align-items: flex-start;
      margin-left: 0.5rem;
    }

    .message-bubble {
      background-color: hsl(0, 0%, 94%);
      color: var(--bulma-text);
      border-bottom-left-radius: 4px;
    }

    .message-hover-actions {
      left: 0;
      right: auto;
    }
  }
}

.message-avatar {
  flex-shrink: 0;
  margin-top: 1.75rem; // Offset to align with message bubble (accounting for header height)
}

.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: hsl(0, 0%, 86%);
  color: var(--bulma-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.message-container {
  max-width: 65%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

// Message header with name and time
.message-header {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  padding-left: 0.5rem;

  .message-sender {
    font-size: 0.75rem;
    font-weight: 600;
    color: hsl(0, 0%, 29%);
  }

  .message-time {
    font-size: 0.625rem;
    color: hsl(0, 0%, 60%);
  }
}

// Align header properly for sent messages
.message-row.is-mine .message-header {
  justify-content: flex-end;
  padding-left: 0;
  padding-right: 0.5rem;
}

.message-bubble-wrapper {
  position: relative;
  display: inline-flex;
  align-items: flex-end;
  margin-bottom: 0.25rem; // Space for reactions
  flex-direction: row-reverse; // Read receipts on left, bubble on right
}

// For sent messages (right-aligned), keep normal order
.message-row.is-mine .message-bubble-wrapper {
  flex-direction: row;
}

// Vertical stack for bubble + image inside the inline-flex wrapper
.message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.message-row.is-mine .message-content {
  align-items: flex-end;
}

.message-bubble {
  padding: 0.5rem 0.875rem;
  border-radius: 18px;
  line-height: 1.4;
  display: inline-block;
  max-width: 100%;
  word-break: break-word;

  p {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }


  :deep(.mention) {
    background-color: rgba(29, 155, 240, 0.15);
    color: hsl(217, 71%, 45%);
    padding: 0.125rem 0.25rem;
    border-radius: 4px;
    font-weight: 600;
  }

  :deep(.message-link) {
    color: hsl(217, 71%, 45%);
    text-decoration: underline;
    word-break: break-all;

    &:hover {
      color: hsl(217, 71%, 35%);
    }
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(u) {
    text-decoration: underline;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0.25rem 0 0.25rem 1.25rem;
    padding: 0;
  }


  :deep(li) {
    margin-bottom: 0.1rem;
  }
}

// Image attachments below message bubble
.message-attachments {
  margin-top: 0.25rem;
}

.message-image-link {
  display: inline-block;
  line-height: 0;
  cursor: pointer;

  &:hover .message-image {
    opacity: 0.85;
  }
}

.message-image {
  max-width: 280px;
  max-height: 200px;
  border-radius: 10px;
  object-fit: contain;
  transition: opacity 0.15s;
}

// Image lightbox overlay
.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lightbox-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 6px;
  cursor: default;
}

.lightbox-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.message-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 0.5rem;
  flex-wrap: wrap;

  .is-edited {
    font-size: 0.625rem;
    color: hsl(0, 0%, 60%);
    font-style: italic;
  }
}

// Align footer properly for sent messages
.message-row.is-mine .message-footer {
  flex-direction: row-reverse;
  padding-left: 0;
  padding-right: 0;
}

.message-reactions {
  display: flex;
  gap: 0.375rem;
  align-items: center;
  flex-wrap: wrap;
}

// Hover actions that appear above message
.message-hover-actions {
  position: absolute;
  top: -32px;
  display: flex;
  gap: 0.125rem;
  align-items: center;
  background: white;
  border-radius: 18px;
  padding: 0.25rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 10;

  .confirm-text {
    margin: 0 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: hsl(0, 0%, 29%);
    white-space: nowrap;
  }
}

.message-row:hover .message-hover-actions,
.message-hover-actions:has(.confirm-text) {
  opacity: 1;
  pointer-events: auto;
}

.hover-action-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: hsl(0, 0%, 40%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &:hover {
    background: hsl(0, 0%, 94%);
    color: var(--bulma-text);
  }

  &.emoji-btn {
    font-size: 1.125rem;
    width: 32px;
    height: 32px;
  }

  &.confirm-btn {
    &:hover {
      background: hsl(141, 71%, 48%);
      color: white;
    }
  }

  &.cancel-btn {
    &:hover {
      background: hsl(348, 100%, 61%);
      color: white;
    }
  }
}


// Message edit mode
.message-edit {
  padding: 0.5rem;
  background: var(--bulma-scheme-main);
  border: 1px solid var(--bulma-border);
  border-radius: 12px;
  margin-bottom: 0.5rem;
}

.reaction-badge {
  background: hsl(0, 0%, 96%);
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 0.25rem 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &.is-mine {
    background: var(--bulma-primary-light);
    border-color: var(--bulma-primary);
  }

  &:hover {
    transform: scale(1.1);
    background: hsl(0, 0%, 92%);
  }

  .reaction-count {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--bulma-text);
    margin-left: 0.125rem;
  }
}

// Add reaction button
.add-reaction-btn {
  background: transparent;
  border: 2px dashed hsl(0, 0%, 80%);
  border-radius: 16px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: hsl(0, 0%, 60%);
  font-size: 0.875rem;

  &:hover {
    background: hsl(0, 0%, 96%);
    border-color: hsl(0, 0%, 70%);
    border-style: dashed;
    transform: scale(1.1);
  }
}


// Composer wrapper
.composer-wrapper {
  padding-top: 1rem;
  position: relative;
}

.mention-input-wrapper {
  position: relative;
}

// Gradient-glow pill composer
.composer-card {
  border-radius: 20px;
  padding: 1.5px;
  background: var(--bulma-primary);
  transition: box-shadow 0.3s ease;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 4px 6px;

  &.is-focused {
    box-shadow: rgba(0, 0, 0, 0.15) 0px 4px 6px;
  }

  &.is-editing {
    background: var(--bulma-warning);
    box-shadow: 0 0 0 1px var(--bulma-warning), rgba(0, 0, 0, 0.15) 0px 4px 6px;
  }
}

// Review tab's status bar lands here (Teleport) beside the tab pills
.toolbar-extras {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  flex-wrap: wrap;
}

// Half-width tabs (Details, Brief) can't fit it beside the pills
.toolbar-extras.is-own-row {
  flex-basis: 100%;
  justify-content: flex-start;
}

.assets-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding-bottom: 1rem;
}

.toolbar-review-extras:empty {
  display: none;
}

// Not enough room beside the pills: give it its own line instead of wrapping
@media screen and (max-width: 1399px) {
  .toolbar {
    flex-wrap: wrap;
  }

  .toolbar-extras {
    flex-basis: 100%;
    justify-content: flex-start;
  }
}

// Phone: tab pills scroll sideways, title row wraps, instead of the whole
// modal overflowing the screen
@media screen and (max-width: 699px) {
  // flex items default to min-width:auto, which lets wide content push the
  // column past the screen
  .deliverable-details-container > .column,
  .tab-content {
    min-width: 0;
  }

  .toolbar .filters {
    max-width: 100%;
    overflow-x: auto;
  }

  // the ledger toolbar turns into a column here; 100% basis would mean height
  .toolbar-extras,
  .toolbar-extras.is-own-row {
    flex: 0 0 auto;
  }

  .modal-card-title {
    flex: 1 1 auto;
    flex-wrap: wrap;
    white-space: normal;
    min-width: 0;
  }
}

.formatting-toolbar {
  display: flex;
  gap: 0.25rem;
  padding: 0.4rem 0.75rem;
  background: var(--bulma-scheme-main);
  border-radius: 18.5px 18.5px 0 0;
  border-bottom: 1px solid var(--bulma-border-weak);

  .toolbar-divider {
    width: 1px;
    background: var(--bulma-border);
    margin: 0.15rem 0.25rem;
  }

  .format-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.2rem 0.45rem;
    height: 1.6rem;
    min-width: 1.6rem;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--bulma-text);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s, background-color 0.15s;

    &:hover {
      opacity: 1;
      background: var(--bulma-scheme-main-bis);
    }

    &.is-active {
      opacity: 1;
      background: var(--bulma-primary);
      color: white;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .icon {
      font-size: 0.7rem;
    }
  }
}

// Composer body holds editor + send button
.composer-body {
  position: relative;
}

// WYSIWYG contenteditable editor
.message-editor {
  border: none;
  border-radius: 18.5px;
  padding: 0.75rem 3rem 0.75rem 1rem;
  min-height: 3rem;
  max-height: 10em;
  overflow-y: auto;
  font: inherit;
  line-height: 1.5;
  outline: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  background: var(--bulma-scheme-main);

  &:empty::before {
    content: attr(data-placeholder);
    color: var(--bulma-text-weak);
    font-size: 0.8rem;
    pointer-events: none;
  }

  &[contenteditable="false"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// When toolbar is showing, flatten editor top corners
.composer-card.is-focused .message-editor {
  border-radius: 0 0 18.5px 18.5px;
}

// When attachment preview is showing without toolbar, flatten editor top corners
.composer-card:has(.attachment-preview) .message-editor {
  border-radius: 0 0 18.5px 18.5px;
}

// When attachment preview shows without toolbar, round its top corners
.composer-card:not(.is-focused) .attachment-preview {
  border-radius: 18.5px 18.5px 0 0;
}

// Circular gradient send button
.send-btn {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--bulma-primary);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: transform 0.15s, opacity 0.15s;
  box-shadow: 0 2px 8px rgba(var(--bulma-primary-rgb), 0.3);

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.is-editing {
    background: var(--bulma-warning);
    color: var(--bulma-warning-invert, rgba(0, 0, 0, 0.7));
    box-shadow: 0 2px 8px rgba(var(--bulma-warning-rgb, 255, 224, 138), 0.4);
  }
}

// Attachment preview strip in composer
.attachment-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: var(--bulma-scheme-main-bis);
  border-bottom: 1px solid var(--bulma-border-weak);
}

.attachment-thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-name {
  font-size: 0.75rem;
  color: var(--bulma-text-weak);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

// Hint text below composer
.composer-hint-row {
  display: flex;
  align-items: center;
}

.composer-hint {
  font-size: 0.7rem;
  color: var(--bulma-text-weak);
  padding: 0.35rem 1rem 0;

  &.editing-hint {
    color: hsl(48, 80%, 40%);
  }
}

.cancel-edit-link {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  color: hsl(348, 80%, 55%);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: hsl(348, 80%, 45%);
  }
}

// Mention chips inside contenteditable
.message-editor :deep(.mention),
.message-editor .mention {
  background-color: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  border-radius: 4px;
  padding: 0.1em 0.3em;
  font-weight: 500;
  white-space: nowrap;
  user-select: all;
}

.mention-suggestions {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--bulma-border);
  border-radius: 8px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
  margin-bottom: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
}

.mention-suggestion {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &.is-selected {
    background-color: var(--bulma-scheme-main-bis);
  }

  &.is-selected {
    background-color: var(--bulma-primary-light);
  }
}

.suggestion-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--bulma-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.suggestion-info {
  flex: 1;
  min-width: 0;
}

.suggestion-name {
  font-weight: 500;
  color: var(--bulma-text);
  font-size: 0.875rem;
}

.suggestion-email {
  font-size: 0.75rem;
  color: var(--bulma-text-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Read receipts styles - left side of message bubble
.read-receipt-avatars-bubble {
  display: inline-flex;
  align-items: center;
  animation: fadeIn 0.3s ease;
  margin-right: 0.125rem; // Tiny gap from bubble
  flex-direction: row-reverse; // Reverse order so count appears on left
}

.read-receipt-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid white;
  background: hsl(0, 0%, 86%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  cursor: default;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.2);
    z-index: 1;
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-initials {
    font-size: 0.5rem;
    font-weight: 600;
    color: white;
    background: hsl(217, 71%, 53%);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Stack avatars with slight overlap
  &:not(:first-child) {
    margin-left: -8px;
  }
}

.read-receipt-more {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid white;
  background: hsl(0, 0%, 94%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 600;
  color: hsl(0, 0%, 48%);
  margin-left: -8px;
  cursor: default;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.assets-tab-content {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.assets-grid {
  display: grid;
  // Cap each column at Bunny's preview.webp width (320px) so the animated
  // hover preview never upscales. Cards left-align inside the modal.
  grid-template-columns: repeat(auto-fill, minmax(220px, 320px));
  gap: 1rem;

  // Dozens of backdrop-filter layers in a scrolling grid make Chrome drop paint
  // tiles (cards render half-blank). Badges use a solid tint here instead.
  :deep(*) {
    backdrop-filter: none !important;
  }

  &.single-column {
    grid-template-columns: minmax(0, 320px);
  }
}

.asset-card-wrapper {
  cursor: pointer;
  position: relative;

  .download-overlay-btn {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.7);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.2s ease;
    z-index: 10;

    .icon {
      font-size: 0.875rem;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.8);
      transform: scale(1.1);
    }

    &.is-loading {
      background-color: var(--bulma-primary);
    }

    &.is-extract-audio {
      right: 3rem;
    }
  }
}
</style>